import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { EProjectPurpose } from "common/enums/ProjectPurpose";
import { EProjectReason } from "common/enums/ProjectReason";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import { EValidationError } from "common/enums/Validation/ValidationErrors";

const EProjectReasonImplementation: Record<EProjectReason, string> = {
	PROTECT: "Je dépose mon projet simplement pour le protéger",
	PROTECT_AND_DEVELOPMENT_AID: "Je dépose mon projet pour le protéger et le soumettre à l'aide au développement",
};

const EProjectPurposeImplementation: Record<EProjectPurpose, string> = {
	FUNDING_APPLICATION: "Le soumettre au comité de sélection de LaDCF (La Diversité du Cinéma Français) afin de prétendre à une aide au développement",
	AUTHENTICITY_PROTECTION: "Pour le protéger et obtenir un certificat d'authenticité",
	PITCH_TO_PROFESSIONALS: "Obtenir une chance de le pitcher à des professionnels, grâce à Neeedco.",
};

const EProjectFormatImplementation: Record<EProjectFormat, string> = {
	court_metrage: "Court-métrage",
	documentaire: "Documentaire",
	long_metrage: "Long-métrage",
	serie: "Série",
};

const EProjectThemeImplementation: Record<EProjectTheme, string> = {
	comedie: "Comédie",
	comedie_romantique: "Comédie romantique",
	comedie_dramatique: "Comédie dramatique",
	biopic: "Biopic",
	thriller: "Thriller",
	film_daction: "Film d'action",
	film_policier: "Film policier",
	film_dhorreur: "Film d'horreur",
	film_historique: "Film historique",
	film_aventure: "Film d'aventure",
	film_documentaire: "Film documentaire",
	science_fiction: "Science fiction",
	film_danimation: "Film d'animation",
	comedie_musicale: "Comédie musicale",
	dramedie: "Dramédie",
	comedie_absurde: "Comédie absurde",
	comedie_sociale: "Comédie sociale",
	thriller_psychologique: "Thriller psychologique",
};

const EValidationErrorImplementation: Record<EValidationError, string> = {
	anchorNotExists: "L'ancrage n'existe pas.",
	arrayNotEmpty: "Ce champ est obligatoire.",
	badContributors: "Les contributeurs ne sont pas valides.",
	badCredentials: "Identifiants incorrects. Veuillez réessayer.",
	buttonLabelIsRequired: "Le libellé du bouton est requis.",
	buttonLinkIsRequired: "Le lien du bouton est requis.",
	choiceNotExists: "Le choix n'existe pas.",
	emailCaseSensitive: "L'adresse e-mail doit être en minuscules.",
	emailNotExists: "L'email n'existe pas.",
	emailNotFound: "L'adresse e-mail que vous avez fournie n'est associée à aucun compte.",
	fileNotExists: "Le fichier n'existe pas.",
	firstNameIsRequired: "Le prénom est requis.",
	invalidLinkedInUrl: "Veuillez entrer une URL LinkedIn valide.",
	invalidPhoneNumber: "Seul les formats français ou interlational sont acceptés.",
	isArray: "Vous devez sélectionner au moins une valeur.",
	isBoolean: "Ce champ est obligatoire.",
	isDate: "Veuillez entrer une date valide.",
	isEmail: "Veuillez entrer une adresse e-mail valide.",
	isEnum: "Ce champ est obligatoire.",
	isInt: "Assurez-vous d'entrer un nombre entier, sans décimales.",
	isNotEmpty: "Ce champ est obligatoire.",
	isNumber: "Ce champ est obligatoire. La valeur doit être un nombre.",
	isNumberString: "Ce champ est obligatoire. La valeur doit être numérique.",
	isObject: "Ce champ est obligatoire.",
	isPositive: "Le nombre doit être positif.",
	isString: "Ce champ est obligatoire.",
	isStrongPassword: "Le mot de passe doit être plus complexe pour des raisons de sécurité.",
	isUrl: "Veuillez entrer une URL valide.",
	lastNameIsRequired: "Le nom est requis.",
	match: "Les valeurs ne correspondent pas.",
	maxLength: "La longueur du texte ne doit pas dépasser {{var1}} caractères.",
	max: "Le nombre doit être inférieur ou égal à {{var1}}.",
	mediaUrlIsRequired: "L'URL du média est requise.",
	minLength: "La longueur du texte doit être d'au moins {{var1}} caractères.",
	min: "Le nombre doit être supérieur ou égal à {{var1}}.",
	mustBeInThePast: "La date doit être dans le passé.",
	passwordNotMatch: "Les mots de passe ne correspondent pas",
	pinCodeNotValid: "Le code PIN n'est pas correct.",
	projectTeamNotExist: "L'équipe de projet n'existe pas.",
	subscriptionPlanNotExists: "La formule de souscription n'existe pas.",
	uniqueEmail: "Veuillez utiliser une adresse e-mail différente, celle-ci est déjà utilisée.",
	uniqueSha256: "Il n'est pas possible de soumettre deux fois le même fichier.",
	wrongDateInterval: "La date de début doit être avant la date de fin.",
	wrongPassword: "Le mot de passe que vous avez saisi est incorrect.",
	isDefined: "Au moins l'un des prénom, nom de famille ou nom de l'entreprise doit être fourni.",
};

const trads = {
	common: {
		cancel: "Annuler",
		logout: "Déconnexion",
		search: "Rechercher",
		yes: "Oui",
		no: "Non",
		save: "Enregistrer",
		back: "Retour",
		delete: "Supprimer",
		edit: "Modifier",
		add: "Ajouter",
		next: "Suivant",
		crew: "L'équipe du film",
	},
	projects: {
		forum: {
			answers: "{{n}} réponses",
			single_answer: "{{n}} réponse",
			no_answers: "{{n}} réponse",
		},
	},
	unit: {
		megabyte: "Mo",
	},
	users: {
		subscription: {
			subscribe: "Passer PRO",
		},
	},
	pages: {
		professional_space: {
			page_title: "Espace professionnel",
			layout: {
				project_title: "Titre",
				archive: "Archiver",
				save_and_quit: "Enregistrer et quitter",
				save_and_publish: "Enregistrer et publier",
				back: "Retour",
				banner: {
					draft_project:
						"<b>Important à savoir avant de publier votre projet :</b>Une fois votre projet mis en ligne, toutes les modifications que vous apporterez seront automatiquement visibles. Il ne sera plus possible de revenir au mode brouillon.<br />👉 Pour expérimenter ou tester des idées, vous pouvez toujours créer un autre projet.",
					published_project: "<b>Ce projet est en ligne.</b>Toutes les modifications ou ajouts que vous effectuez sont instantanément visibles par la communauté.",
				},
			},
			tabs: {
				information: {
					title: "Informations",
					submit: "Enregistrer",
					save: "Sauvegarder les informations",
					saved: "Informations enregistrées",
					creative_source: {
						title: "Inspiration ou œuvre d'origine",
						description: "Ajoutez une ou plusieurs images avec une courte description (film, livre, affiche, etc.).",
						add: "Ajouter une inspiration",
						create_creative_source_modal: {
							title: "Ajouter une inspiration",
							image: "Image de l'inspiration",
							image_description: "Cliquer pour télécharger ou glisser et déposer",
							description: "Description",
							description_additional_info: "50 caractères maximum",
							cancel: "Annuler",
							create: "Ajouter l'inspiration",
						},
						edit_creative_source_modal: {
							title: "Modifier une inspiration",
							image: "Image de l'inspiration",
							image_description: "Cliquer pour télécharger ou glisser et déposer",
							description: "Description",
							description_additional_info: "50 caractères maximum",
							cancel: "Annuler",
							edit: "Modifier l'inspiration",
						},
					},
					sub_tabs: {
						content: "Contenu",
						project_leader: "Porteur du projet",
						project_leader_subtitle: "Présentez le ou la porteur·se du projet*",
						project_leader_description:
							"Ces informations seront affichées pour que les visiteurs sachent à qui appartient le projet. Cela peut être une personne ou une entreprise.",
						documents: "Documents",
						contact_and_socials: "Contact & réseaux",
						contact_button: "Bouton de contact",
					},
					poster_container: {
						buttons: {
							regenerate: "Regénérer une affiche IA",
							deposit: "Déposer mon affiche",
						},
					},
					project_info: {
						project_title: "Titre du projet",
						project_subtitle: "Sous-titre du projet",
						project_title_placeholder: "100 caractères maximum",
						project_format: "Format",
						production_stage: "Stade de production",
						looking_for: "En recherche de",
						project_pitch: "Pitch du projet",
						project_pitch_additional_info:
							"Veuillez fournir une description concise et attrayante. Cette description sera utilisée pour mettre en avant votre projet sur la plateforme.",
						project_pitch_placeholder: "Pitch (150-400 caractères)",
						drag_and_drop: "Glisser ou cliquer ici pour télécharger votre document",
						drag_and_drop_profile_picture: "Glisser ou cliquer ici pour télécharger votre image de profil",
						scenario: "Synopsis et/ou éléments artistiques",
						project_leader: "Porteur du projet*",
						first_name: "Prénom",
						last_name: "Nom",
						enterprise: "Entreprise",
						profile_picture: "Image du profil",
					},
				},
				votes: {
					title: "Sondages",
					create_vote: "Créer un sondage",
					see_more: "Voir plus",
					see_less: "Voir moins",
					confirm_vote: "Confirmer le sondage",
					tags: {
						in_progress: "jour",
						ended: "Terminé",
					},
					create_vote_modal: {
						title: "Créer un sondage",
						vote_title: "Titre",
						vote_description: "Description",
						vote_end_date: "Date de fin du sondage",
						vote_options: "Options de sondage",
						vote_add_option: "Ajouter une option",
						vote_option_max_length: "Option {{index}} (70 caractères max)",
						vote_option_max_length_error: "Un ou plusieurs champs du sondage dépassent la limite de 70 caractères. Veuillez vérifier les champs et réessayer.",
						cancel: "Annuler",
						create_vote: "Créer le sondage",
					},
					edit_vote_modal: {
						title: "Modification d'un sondage",
						vote_title: "Titre",
						vote_description: "Description",
						vote_end_date: "Date de fin du sondage",
						vote_options: "Options de sondage",
						vote_add_option: "Ajouter une option",
						vote_option_max_length: "Option {{index}} (70 caractères max)",
						vote_option_max_length_error: "Un ou plusieurs champs du sondage dépassent la limite de 70 caractères. Veuillez vérifier les champs et réessayer.",
						cancel: "Annuler",
						create_vote: "Modifier le sondage",
					},
					delete_vote_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression de ce sondage ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
				},
				actions: {
					title: "Le Mur",
					create_action: "Créer une action",
					progress_tags: {
						pending: "À venir",
						in_progress: {
							less_than_one_hour: "<1 heure",
							single_hour_left: "{{n}} heure",
							several_hours_left: "{{n}} heures",
							single_day_left: "{{n}} jour",
							several_days_left: "{{n}} jours",
						},
						ended: "Terminé",
					},
					create_action_modal: {
						title: "Action",
						action_title_description: "50 caractères maximum",
						action_title: "Titre",
						action_description: "Description",
						action_image: "Image de présentation de l'action",
						action_image_description: "Cliquez ou glissez-déposez pour ajouter une photo",
						action_button_label: "Nom du bouton",
						action_button_link: "Lien de redirection du bouton",
						action_start_date: "Date de début de l'action",
						action_end_date: "Date de fin de l'action",
						create_action: "Créer l'action",
						cancel: "Annuler",
					},
					edit_action_modal: {
						title: "Action",
						action_title: "Titre",
						action_description: "Description",
						action_image: "Image de présentation de l'action",
						action_image_description: "Cliquez ou glissez-déposez pour ajouter une photo",
						action_button_label: "Nom du bouton",
						action_button_link: "Lien de redirection du bouton",
						action_start_date: "Date de début de l'action",
						action_end_date: "Date de fin de l'action",
						create_action: "Modifier l'action",
						cancel: "Annuler",
					},
					delete_action_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression de cette action «{{name}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
				},
				forum: {
					title: "Forum",
					create_post: "Créer une discussion",
					create_post_modal: {
						title: "Créer un forum",
						post_title: "Titre",
						post_description: "Description",
						post_image: "Image",
						post_image_description: "Cliquez ou glissez-déposez pour ajouter une photo",
						create_post: "Créer la discussion",
						cancel: "Annuler",
					},
					edit_post_modal: {
						title: "Modification d'un forum",
						post_title: "Titre",
						post_description: "Description",
						post_image: "Image",
						post_image_description: "Cliquez ou glissez-déposez pour ajouter une photo",
						create_post: "Modifier la discussion",
						cancel: "Annuler",
					},
					delete_forum_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression de ce forum «{{name}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
				},
				casting: {
					title: "Casting",
					create_casting: "Ajouter un membre",
					create_casting_modal: {
						title: "Ajouter un membre au casting",
						name: "Nom du personnage",
						first_name: "Prénom",
						last_name: "Nom",
						drag_and_drop: {
							image: "Image de l'acteur",
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
						},
						create_casting: "Ajouter un membre",
						cancel: "Annuler",
					},
					edit_casting_modal: {
						title: "Modification d'un membre du casting",
						name: "Nom du personnage",
						first_name: "Prénom",
						last_name: "Nom",
						drag_and_drop: {
							image: "Image de l'acteur",
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
						},
						create_casting: "Modifier un membre",
						cancel: "Annuler",
					},
					delete_casting_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression de ce membre «{{name}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
					casting_card: {
						played_by: "Interprété(e) par",
					},
					tabs: {
						forum: "Aidez-nous",
						vote: "Votez",
						casting: "Casting confirmé",
					},
				},
				media: {
					title: "Médias",
					subtitle: "Ordre d'affichage des médias",
					description: "L'ordre d'affichage dépend de l'ordre de publication des médias. Pensez à bien les organiser avant de les ajouter.",
					add_media: "Ajout d'un média",
					add_media_modal: {
						title: "Ajouter un média",
						add_media: "Ajouter le média",
						cancel: "Annuler",
						type: "Type de média",
						url: "Lien du média",
						IMAGE: "Image",
						VIDEO: "Vidéo",
						URL: "Lien",
						media_title: "Titre du média",
						media_title_max_length: "100 caractères maximum",
						drag_and_drop: {
							image: "Image",
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
							image_link: "Image de présentation (facultatif)",
						},
					},
					delete_media_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression de ce média «{{name}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
					edit_media_modal: {
						title: "Modification d'un média",
						edit_media: "Modifier le média",
						cancel: "Annuler",
						type: "Type de média",
						url: "Lien du média",
						IMAGE: "Image",
						VIDEO: "Vidéo",
						URL: "Lien",
						media_title: "Titre du média",
						media_title_max_length: "100 caractères maximum",
						drag_and_drop: {
							image: "Image",
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
							image_link: "Image de présentation (facultatif)",
						},
					},
				},
				team: {
					title: "Équipe",
					create_role: "Créer un rôle",
					create_role_modal: {
						title: "Création d'un rôle",
						role_name: "Nom du rôle",
						role_name_max_length: "100 caractères maximum",
						role_order: "Numéro d'ordre",
						role_members: "Membre rattaché au rôle",
						add_member: "Ajouter un membre",
						create_role: "Créer le rôle",
					},
					edit_role_modal: {
						title: "Modification d'un rôle",
						role_name: "Nom du rôle",
						role_name_max_length: "100 caractères maximum",
						role_order: "Numéro d'ordre",
						role_members: "Membre rattaché au rôle",
						add_member: "Ajouter un membre",
						edit_role: "Modifier le rôle",
					},
					delete_role_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression du rôle «{{role}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
					add_member_modal: {
						title: "Ajouter un membre",
						first_name: "Prénom",
						last_name: "Nom",
						enterprise: "Entreprise",
						profile_picture: "Image du profil",
						add_member: "Ajouter un membre",
						drag_and_drop: {
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
						},
					},
					edit_member_modal: {
						title: "Modification d'un membre",
						first_name: "Prénom",
						last_name: "Nom",
						enterprise: "Entreprise",
						profile_picture: "Image du profil",
						add_member: "Modifier le membre",
						drag_and_drop: {
							description: "Cliquez ou glissez-déposez pour ajouter une photo",
						},
					},
					delete_member_modal: {
						title: "Confirmation de supression",
						text_1: "Confirmez-vous la suppression du membre «{{member}}» ? ",
						text_2: "Attention, cette opération ne pourra pas être annulée.",
						confirm: "Confirmer",
						cancel: "Annuler",
					},
				},
			},
		},
		admin_dashboard: {
			page_title: "Projets déposés",
			title: "Projets déposés",
			filters: {
				all_projects: "Tous les projets",
				accepted: "Retenu",
				refused: "Non retenu",
				studying: "En cours d'étude",
				archived: "Archives",
			},
			table: {
				need_to_be_studied: {
					yes: "Oui",
					no: "Non",
				},
				headers: {
					project_name: "Nom du projet",
					author: "Auteur",
					status: "Statut",
					date: "Date",
				},
				buttons: {
					cv: "Télécharger le CV",
					scenario: "Télécharger le scénario",
					data: "Informations sur le projet",
					status: "Statut",
					archive: "Archiver",
					restore: "Restaurer",
					anchor: "Ancrer",
				},
				no_cv: "Aucun CV",
				no_scenario: "Aucun scénario",
			},
			drawer: {
				project_leader: "Porteur de projet",
				poster: {
					regenerate: "Regénérer une affiche",
					pending: "L'affiche est en cours de génération",
					failed: "L'image n'a pas pu être générée par IA. Cela peut être dû à un lien dans votre pitch ou à l'utilisation de mots pouvant être inappropriés.",
				},
				author: "Auteur",
				infos: {
					title: "Informations",
					publication_date: "Publication :",
					genre: "Genre :",
					format: "Format :",
					reason: "Raison :",
					accepted_nota_bene: "NB : Votre projet est retenu pour être soumis au Comité de Sélection de LaDCF.",
				},
				buttons: {
					cv: "Télécharger le CV",
					scenario: "Télécharger le scénario",
					archive: "Archiver",
					restore: "Restaurer",
				},
			},
		},
		project: {
			page_title: "Page du projet",
			production_stage: "Stade de production :",
			project_needs: "En recherche de :",
			project_leader: "Porteur de projet :",
			publication: "Publication :",
			theme: "Genre :",
			format: "Format :",
			tabs: {
				votes: "Sondages",
				actions: "Le Mur",
				forum: "Forum",
				casting: "Casting",
				media: "Média",
				team: "Équipe",
			},
			buttons: {
				readMore: "Lire la suite",
				readLess: "Lire moins",
				edit_project: "Modifier le projet",
			},
		},
		featuredProjects: {
			common: {
				casting: {
					coming_soon: {
						title: "Bientôt, aidez-nous à imaginer le casting du film",
					},
					forum: {
						title: "Forum du casting",
					},
				},
				forum: {
					title: "Forum du projet",
					anwsers: "{{n}} réponses",
					single_anwser: "{{n}} réponse",
				},
				vote: {
					title: "Vote en cours",
				},
				previous_vote: {
					title: "Vote passé",
				},
			},
			grand_saut: {
				page_title: "Le grand saut",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 10 mai 2024",
						},
						genre: {
							key: "Genre :",
							value: "Comédie romantique",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Écriture de la V1",
				},
				project_needs: {
					label: "En recherche :",
					value: "Rôles principaux, financement",
				},
				content: {
					title: "Le grand saut",
					subtitle: "L'amour rend aveugle, le suicide va leur rendre la vue",
					description:
						"Le Grand Saut raconte l'histoire d'Antoine et Lucie, deux âmes brisées que tout oppose, réunies par un étrange point commun : leur désir d'en finir. Leur rencontre dans une ambulance, après avoir chacun échoué leur tentative de suicide, va faire basculer leur vie ou leur mort….  Entre ascensions nocturnes, confrontations explosives et dialogues empreints d'ironie mordante, leur cheminement commun les entraîne dans une évasion surréaliste. Au fil de leurs mésaventures, ils se dévoilent l'un à l'autre et affrontent leurs blessures profondes. À travers leur quête désespérée, ce duo dysfonctionnel met en lumière la force de l'instinct de survie et l'étrange pouvoir de la connexion humaine, même dans les moments les plus sombres.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "legrandsaut@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description: "Candidatez pour devenir l'un des trois premiers lecteurs de la V1 (Première version du scénario) / envoyez nous un mail.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "legrandsaut@clapaction.com",
						},
					},
				},
				casting: {
					title: "Devenez une voix dans le casting",
					description:
						"Dans notre espace forum, la directrice de casting et le réalisateur vous invitent à contribuer en suggérant vos choix d'acteurs pour les rôles décrits dans leurs annonces. À vous de jouer !",
					button: "Voir le forum",
				},
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Anthony Granier",
					},
					referent_agent: {
						role: "Agent référent :",
						name: "Isabelle Frère / If Only",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Anthony Granier", "Sophie Garric"],
					},
					producers: {
						role: "Producteur(s) :",
						names: ["LaDCF (La Diversité du Cinéma)", "Watch'Us Productions"],
					},
				},
			},
			malgre_moi: {
				page_title: "Malgré moi",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 10 mai 2024",
						},
						genre: {
							key: "Genre :",
							value: "Thriller psychologique",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Actuellement en casting",
				},
				project_needs: {
					label: "En recherche :",
					value: "Rôles secondaires, financements",
				},
				content: {
					title: "Malgré moi",
					subtitle: "Le pouvoir du deni",
					description:
						"Théo, un homme marqué par un incendie qui a bouleversé son enfance, voit son monde s'effondrer lorsque sa femme disparaît sans explication. Entre séances chez un psychiatre et flashbacks douloureux, il tente de comprendre ce qui a provoqué cet abandon soudain. Obsédé par la moindre trace de son passage, il traque des indices, mais plus il cherche, plus la réalité se brouille. Ses amis tentent de l'accompagner dans sa quête de vérité, mais Théo doute de tout, même d'eux. A-t-elle choisi de le quitter ou quelqu'un l'a-t-il éloignée de lui ? Pourquoi personne ne semble vouloir lui dire la vérité ? Marine a disparu. Mais où est-elle ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "malgremoi@clapaction.com",
						},
					},
				},
				creative_source: {
					description: "D'après le roman  « Malgré Nous » de Claire Norton.",
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"Vous avez occupé le poste de chef déco sur un long-métrage déjà diffusé ? Si Malgré Moi vous intrigue, contactez-nous ! Nous enverrons le scénario aux premiers candidats afin de découvrir, après lecture, votre vision artistique (moodboard, propositions, etc.) / envoyez nous un mail.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "malgremoi@clapaction.com",
						},
					},
				},
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "CB Partners",
					},
					referent_agent: {
						role: "Agent référent :",
						name: "Ghislaine Gracieux",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Laurent Bouhnik"],
					},
					directors: {
						role: "Réalisateur(rice) :",
						names: ["Laurent Bouhnik"],
					},
					casting_directors: {
						role: "Directeur de casting :",
						names: ["Okinawa Guerard  / Encore un casting"],
					},
					producers: {
						role: "Producteur(s) :",
						names: ["Carlos Bedran", "Sarah Lelouch"],
					},
					productions: {
						role: "Productions:",
						names: ["CB Partners", "LaDCF (La Diversité du Cinéma)"],
					},
				},
			},
			kdanse: {
				page_title: "KDanse",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 10 mai 2024",
						},
						genre: {
							key: "Genre :",
							value: "Dramedie",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Écriture du séquenceur",
				},
				project_needs: {
					label: "En recherche :",
					value: "Co-autrice, rôles principaux, financement",
				},
				content: {
					title: "En K-Danse",
					subtitle: "Les champs de la résilience",
					description:
						"Dans les montagnes paisibles de Haute-Savoie, une famille lutte contre la maladie, les dettes et le poids du silence. David, le fils cadet, agriculteur dévoué, voit son monde bouleversé par le retour inattendu d'Émilie, sa sœur, devenue Emile, transgenre et passionnée de K-Pop…. Avec une idée aussi audacieuse qu'absurde, Émile propose de participer à un concours de K-Pop pour sauver la ferme familiale. Entre confrontations et réconciliations, cette aventure inattendue dévoile leurs blessures et leur résilience. Mais le temps joue contre eux : sauront-ils triompher à ce concours et à de leurs propres conflits ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "enkdanse@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"Nous proposer vos groupes de K-Pop préférés afin qu'on les sollicite pour nous accompagner dans la composition musicale de notre film. Si vous avez d'ores et déjà des idées, n'hésitez pas à nous envoyer un mail.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "enkdanse@clapaction.com",
						},
					},
				},
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Philippe Gray",
					},
					referent_agent: {
						role: "Agent référent :",
						name: "Fred Malek / Agence Caractère",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Philippe Gray"],
					},
					directors: {
						role: "Réalisateur(rice) :",
						names: ["Philippe Gray"],
					},
				},
			},
			snow_vision: {
				page_title: "SNOW VISION, Une Neige Perpetuelle",
				header: {
					properties: {
						publication: {
							key: "Publication:",
							value: "le 03 mars 2025",
						},
						genre: {
							key: "Genre:",
							value: "Film Documentaire",
						},
						format: {
							key: "Format:",
							value: "Long-métrage",
						},
					},
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Désirée Giorgetti",
					},
					authors: {
						role: "Auteur(e)(s) et Co-auteur(e)(s) :",
						names: ["Désirée Giorgetti", "Michel Segal"],
					},
					directors: {
						role: "Réalisateur :",
						names: ["Michael Segal"],
					},
					producers: {
						role: "Productrice :",
						names: ["Sarah Lelouch"],
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "En développement",
				},
				project_needs: {
					label: "En recherche :",
					value: "Chef opérateur/trice, étalonneur/euse, partenaires, financements, distributeurs, diffuseurs",
				},
				content: {
					title: "SNOW VISION, Une Neige Perpetuelle",
					subtitle: "Un regard voilé, une histoire lumineuse",
					description:
						"Avec sincérité et courage, Désirée nous entraîne dans son parcours : de la confusion initiale face à un monde constamment voilé de points scintillants, à la révélation tardive du nom de son handicap. Ce film est une immersion intime dans son quotidien, ses défis et sa résilience, mais aussi un cri du cœur pour sensibiliser à une maladie méconnue.<i>Snow Vision</i> est avant tout une œuvre visuelle audacieuse. Le film propose une expérience esthétique unique : reproduire à l'écran la perception altérée de Désirée et de toutes celles et ceux qui vivent avec ce syndrome. Chaque image devient une invitation à voir le monde à travers leurs yeux, brouillant les repères visuels habituels pour plonger le spectateur dans une réalité en perpétuel mouvement. Plus qu’un documentaire, <i>Snow Vision</i> est une nouvelle écriture cinématographique, offrant une lecture inédite de la vie, de la résilience et de l’acceptation, racontées et filmées comme jamais auparavant.<i>Snow Vision</i> pose une question essentielle : comment donner du sens à la réalité quand chaque instant est recouvert d’une neige perpétuelle ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "snowvision@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"📢 APPEL À TÉMOINS & EXPERTS – PARTICIPEZ AU DOCUMENTAIRE SNOW VISION 🎬\n\n🔍 Vous êtes concerné(e) par le syndrome de la neige visuelle ?\n\n👉 Vous êtes atteint(e) de ce syndrome ?\n\n👉 Vous connaissez quelqu’un qui en souffre et souhaitez partager son expérience ?\n\n👉 Vous êtes un professionnel de santé spécialisé dans ce domaine ou avez des connaissances approfondies sur cette maladie rare ?\n\nNous recherchons des témoignages, des regards, des expertises médicales et scientifiques pour enrichir ce projet unique et aider à faire connaître ce syndrome encore méconnu.\n\n🎥 Devant ou derrière la caméra, vous pouvez contribuer à cette aventure !\n\n📩 Contactez-nous dès maintenant pour en savoir plus et participer au documentaire.\n\nEnsemble, donnons de la visibilité à ceux qui voient le monde autrement.",
				},
				casting: {
					title: "Casting confirmé",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			bataillon_silencieux: {
				page_title: "Le Bataillon Silencieux",
				header: {
					properties: {
						publication: {
							key: "Publication:",
							value: "le 13 mars 2025",
						},
						genre: {
							key: "Genre:",
							value: "Film d'aventure",
						},
						format: {
							key: "Format:",
							value: "Long-métrage",
						},
					},
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Thierry MAUVIGNIER",
					},
					director: {
						role: "Réalisateur :",
						name: "Thierry MAUVIGNIER",
					},
					assistant_director: {
						role: "Premier Assistant Réalisateur :",
						name: "Romain Rouan",
					},
					scenario: {
						role: "Scénario :",
						names: ["Thierry Mauvignier", "Joël Bassaget"],
					},
					director_of_photography: {
						role: "Directeur de la photographie :",
						name: "Thierry Pouget",
					},
					storyboard: {
						role: "Storyboard:",
						name: "Christophe-Alexis Perez",
					},
					chief_editor: {
						role: "Chef monteur :",
						name: "Christophe Pinel",
					},
					sound_engineer: {
						role: "Ingénieur du son :",
						name: "Antoine Delfandre",
					},
					costume_design: {
						role: "Création des costumes :",
						name: "Jessica McEvoy",
					},
					visual_effect_manager: {
						role: "Responsable des effets visuels :",
						name: "Séverine De Wever",
					},
					decoration_accessories: {
						role: "Décoration accessoires :",
						name: "Paul Chapelle",
					},
					composer: {
						role: "Compositeur :",
						name: "Romain Warret",
					},
					cascade_adjuster: {
						role: "Régleur cascade :",
						name: "Michel Bouis",
					},
					dogs_animal_trainer: {
						role: "Dresseur d'animaux (chiens) :",
						name: "Juliette Mazingarbe",
					},
					horses_animal_trainer: {
						role: "Dresseur d'animaux (chevaux) :",
						name: "Lucie Colas",
					},
					production_director: {
						role: "Directeur de production :",
						name: "Pascal Lamargot",
					},
					production_manager: {
						role: "Chargé de production :",
						name: "Constantin Martinez",
					},
					producer: {
						role: "Producteur :",
						name: "Thierry Mauvignier",
					},
					co_producer: {
						role: "Co-producteur :",
						names: ["Géraud Ponroy", "LA CAVE À FILMS"],
					},
					executive_producer: {
						role: "Production exécutive :",
						names: ["Gildas Nivet", "Grenouille Productions"],
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Fin de développement - Début de recherche de financements",
				},
				project_needs: {
					label: "En recherche :",
					value: "Partenaires, distributeurs, diffuseurs",
				},
				content: {
					title: "Le Bataillon Silencieux",
					subtitle: "Une course contre la montre. Un combat contre la nature. Une mission pour l'Histoire.",
					description:
						"Inspiré d’une histoire vraie méconnue, Le Bataillon Silencieux retrace la mission secrète du capitaine Louis Moufflet et du lieutenant René Haas en pleine Première Guerre mondiale. Leur objectif : parcourir 10 000 kilomètres à travers l’Alaska et l’Atlantique déchaîné pour ramener 450 chiens de traîneaux, ultime espoir pour ravitailler les tranchées enneigées. Aux côtés de Scotty Allan, légendaire musher (conducteur d’attelage), ils affrontent le froid extrême, les tempêtes et la menace ennemie, portés par une course contre la montre où chaque chien devient un allié précieux. Mais comment ces hommes et leurs chiens sont-ils parvenus à changer le cours de l’Histoire ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "lebataillonsilecieux@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"📢 APPEL À TÉMOIGNAGES – Vivez l’aventure avec Le Bataillon Silencieux !\n\nVous avez déjà conduit un traîneau à chiens ou vécu une expérience marquante avec ces incroyables compagnons du Grand Nord ? ❄️🐕\n\nNous sommes en pleine écriture du film Le Bataillon Silencieux, inspiré d’une histoire vraie, et nous cherchons des anecdotes authentiques pour enrichir certaines scènes. Sensations fortes, liens avec les chiens, moments inoubliables ou défis inattendus... racontez-nous votre expérience !\n\nVotre vécu pourrait donner encore plus de réalisme et d’émotion à cette aventure cinématographique. ⏳🎬\n\n📩 Contactez-nous dès maintenant",
				},
				casting: {
					title: "Casting confirmé",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			rajeunir_ou_mourir: {
				page_title: "Rajeunir ou mourir",
				header: {
					properties: {
						publication: {
							key: "Publication:",
							value: "le 17 mars 2025",
						},
						genre: {
							key: "Genre:",
							value: "Comédie Sociale",
						},
						format: {
							key: "Format:",
							value: "Long-métrage",
						},
					},
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Sylvain Saint Jalmes",
					},
					director: {
						role: "Réalisateur :",
						name: "Sylvain Saint Jalmes",
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Réécriture du scénario",
				},
				project_needs: {
					label: "En recherche :",
					value: "co-auteur, casting, producteurs, partenaires financiers",
				},
				content: {
					title: "Rajeunir ou mourir",
					subtitle: "Ils voulaient rajeunir... et ont découvert que le meilleur restait à venir.",
					description:
						"Et si une simple pilule pouvait défier le temps… seriez-vous prêt à en payer le prix ? Quatre amis quinquagénaires, ne se reconnaissant pas dans l’étiquette « Seniors » de la société, mettent la main sur un stock de pilules miracle permettant de rajeunir. Plutôt que d'en prendre eux-mêmes, ils flairent une opportunité en or : lancer un business aussi lucratif qu’illégal. Le seul « Hic » est que ce commerce parallèle est la chasse gardée d’une organisation Chinoise d’imports-exports. Portés par leur insouciance et leur soif de liberté, ils mettent le pied dans un engrenage qu’ils ne vont absolument pas maitriser. Rajeunir ou Mourir interroge, avec humour et mordant, notre obsession moderne pour l’apparence et la peur de vieillir. Faut-il vraiment rester jeune pour être heureux ? Ou suffit-il simplement de refuser qu'on vous dise le contraire ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "rajeunir@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"🎬 Appel à auteurs expérimentés !\n\nVous êtes scénariste et avez déjà écrit ou co-écrit un ou plusieurs films diffusés à la télévision et/ou au cinéma ? ✍️✨\n\nNous recherchons un auteur chevronné pour participer à la réécriture de Rajeunir ou Mourir, un projet ambitieux mêlant réflexion sur le temps et suspense captivant.\n\n📩 Intéressé(e) ? Contactez-nous dès maintenant !",
				},
				casting: {
					title: "",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			potus: {
				page_title: "POTUS 2.0",
				header: {
					properties: {
						publication: {
							key: "Publication:",
							value: "le 24 mars 2025",
						},
						genre: {
							key: "Genre:",
							value: "Thriller",
						},
						format: {
							key: "Format:",
							value: "Long-métrage",
						},
					},
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteuse de projet :",
						role_2: "Porteuse de projet, idée originale :",
						name: "Clara Lecoeur",
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "En développement",
				},
				project_needs: {
					label: "En recherche :",
					value: "Une ou un co-scénariste d’expériences / Producteur",
				},
				content: {
					title: "POTUS 2.0",
					subtitle: "Qui contrôle la vérité, contrôle le monde",
					description:
						"Lors d’un sommet du G20, Marie Lecornut, journaliste de renom et épouse du président français, assiste à une scène troublante : Mickey Path, le président des États-Unis, s’éclipse discrètement d’un dîner officiel pour une rencontre secrète. Ce qu’elle voit cette nuit-là va changer le cours de l’histoire. Des rumeurs circulent depuis des mois : et si Path n’était pas seulement un président imprévisible, mais un espion infiltré au plus haut sommet du pouvoir américain ? Alors que la France est mise à l’écart sur la scène internationale et que son mari joue sa réélection, Marie convainc Laurent Lecornut d’agir. Avec l’aide de Nia Caldwell, une sénatrice démocrate déterminée à renverser Path, une traque commence. Agents infiltrés, cyber-espionnage, chantage politique : si la vérité éclate, un nouvel ordre mondial pourrait être réécrit. « Qui détient la vérité, contrôle le monde. »",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "potus@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"📢 APPEL AUX PROFESSIONNELS DU CINÉMA 🎬\n\nNéophyte, mais passionné, j’ai des histoires à raconter. La première ? POTUS 2.0, un thriller politique ancré dans l’actualité, où espionnage et pouvoir s’entremêlent.\n\nAujourd’hui, je cherche des professionnels d’expérience pour m’aider à développer ce projet et lui donner vie.\n\n📌 Vous êtes scénariste, réalisateur ou producteur ?\n\n📌 Le sujet vous intrigue ?\n\n📌 Envie d’explorer ensemble ce projet ambitieux ?\n\n👉 Contactez-moi ! 🎥✍️🔥",
				},
				casting: {
					title: "Casting",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			club_vip: {
				page_title: "CLUB VIP",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 31 mars 2025",
						},
						genre: {
							key: "Genre :",
							value: "Comédie Sociale",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				creative_source: {
					description: "Librement adapté du roman de Luc Leprêtre. (Editions Anne Carrière)",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						role2: "Auteur :",
						name: "Luc Leprêtre",
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "En réecriture",
				},
				project_needs: {
					label: "En recherche :",
					value: "co-auteur d’expérience, producteurs, comédiens",
				},
				content: {
					title: "CLUB VIP",
					subtitle: "Vous aussi devenez prioritaire !",
					description:
						"Comédie sociale dramatique, entre feel-good movie, satire douce et drame humain, CLUB VIP suit Aminata, Samy et Jérémy, trois amis en fauteuil roulant, brillants mais mis à l’écart du monde professionnel malgré leurs diplômes et leur motivation. Lassé de vivre aux crochets de sa mère, Jérémy, débrouillard et un brin provocateur, a une idée : et si leur handicap devenait leur business ? Il propose de créer le Club VIP, un service décalé où ils mettent à profit leurs 'privilèges' d’accès prioritaire — dans les magasins, les musées, les parcs d’attractions — au bénéfice de clients pressés. Ce projet, à la frontière entre revendication sociale et coup de poker, va bouleverser leur quotidien.  VIP, c’est l’histoire d’une revanche sur l’invisibilité, une fable moderne sur l’inclusion, portée par l’amitié, l’humour noir, l’espoir… et une bonne dose d’autodérision.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "vip@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				actions: [
					{
						title: "🎬 RECHERCHE PRODUCTEUR.TRICE ENGAGÉ.E",
						description:
							"Envie d’une aventure qui a du sens ?\n\nD’un film qui bouscule les codes, change les regards et donne la parole à ceux qu’on voit trop peu à l’écran ?\n\nJe cherche un.e producteur.trice prêt.e à défendre un projet à la fois drôle, tendre, provocant, et profondément humain.\n\nUn film sur le handicap, le travail, l’amour, la sexualité, l’amitié…\n\nUn film sur la place qu’on laisse – ou pas – à la différence.\n\nSi, comme moi, vous pensez que le cinéma peut faire bouger les lignes,\n\nalors on est faits pour se rencontrer.\n\nOn a besoin de vous pour que cette histoire existe. Pour de vrai.",
						buttons: {
							contact: {
								label: "🤝 Rejoignez le Club ! Contactez l’équipe du film",
								email: "vip@clapaction.com",
							},
						},
					},
					{
						title: "✍️ RECHERCHE AUTRICE / AUTEUR EXPÉRIMENTÉ.E",
						description:
							"Envie d’écrire autrement ? D’aller là où vous n’êtes pas encore allé.e ?\n\nJe cherche un.e co-auteur.e pour embarquer sur un projet fort, humain, sensible et drôle.\n\nTrois personnages. Trois jeunes adultes brillants, attachants, drôles… et en fauteuil.\n\nIls vivent, aiment, doutent, galèrent, rêvent. Comme tout le monde.\n\nMais avec un handicap qui bouscule tout : le quotidien, les envies, les regards, les normes.\n\nCe projet, c’est l’occasion de raconter une histoire différente, pleine de nuances, sans pathos ni clichés.\n\nUn défi d’écriture, oui — mais on le relèvera ensemble, dans la joie, la confiance et la complicité.\n\n🎬 Si vous aimez les personnages complexes, les dialogues vrais, et les récits qui ont quelque chose à dire…",
						buttons: {
							contact: {
								label: "Rejoignez le Club ! Contactez l’équipe du film",
								email: "vip@clapaction.com",
							},
						},
					},
				],
				casting: {
					title: "Casting",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			trainee: {
				page_title: "Trainée de poudre",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 07 avril 2025",
						},
						genre: {
							key: "Genre :",
							value: "Dramédie",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "MARZA",
					},
					author: {
						role: "Auteur/Réalisateur :",
						name: "Marza",
					},
					producer: {
						role: "Producteur :",
						name: "MARZA FILMS",
					},
					cinema_operator: {
						role: "Chef opérateur :",
						name: "Julien Jauffret",
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "En réecriture",
				},
				project_needs: {
					label: "En recherche :",
					value: "co-producteur(s), partenaires, distributeurs, diffuseurs",
				},
				content: {
					title: "Trainée de poudre",
					subtitle: "Elle voulait passer inaperçue. Elle est devenue virale. Une rumeur. Un clic. Une vie en cendres.",
					description:
						"Lina pensait pouvoir tourner la page. Nouveau lycée, nouvelle vie, et surtout, Noah. Avec lui, tout semble possible. Jusqu’à ce que son passé refasse surface. Une rumeur. Un secret dévoilé. Et soudain, tout s’effondre. Lina pensait pouvoir tourner la page. Nouveau lycée, nouvelle vie, et surtout, Noah. Avec lui, tout semble possible. Jusqu’à ce que son passé refasse surface. Une rumeur. Un secret dévoilé. Et soudain, tout s’effondre. Noah doute, les regards changent, les réseaux s’embrasent. Dans ce lycée, la réputation est une bombe à retardement. On la juge pour sa jupe, on la traque pour son passé, on l’exhibe comme un hashtag. L’amour devient emprise, l’amitié se fissure, et le harcèlement… se viralise. Tout commence par un regard. Puis une vidéo. Une insulte. Une gifle. Et une explosion. Et si, dans un monde où l’image est reine, il suffisait d’un like pour tuer une réputation… voire une vie ?",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "trainee@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				actions: [
					{
						title: "Action",
						description:
							"Je cherche aujourd’hui à collaborer avec un·e ado (entre 15 et 18 ans) qui vit ou observe de près la réalité du lycée aujourd’hui, ses codes, ses tensions, ses réseaux. Pas besoin d’être scénariste, juste authentique, curieux·se et volontaire pour discuter.\n\nL’idée : m’aider à affiner certains dialogues, personnages ou situations, pour que le film soit juste, crédible et percutant pour votre génération.\n\n📍 Rencontres possibles à distance ou en présentiel\n\n💬 Confidentialité et bienveillance assurées\n\n🎥 Si tu t’intéresses au cinéma, à l’écriture ou à la société, on aura sûrement beaucoup à se dire.\n\n📩 Contacte l’équipe du film (ou tes parents peuvent le faire)\n\nMerci d’avance pour ta voix, ton regard et ta sincérité.",
						buttons: {
							contact: {
								label: "Contactez l’équipe du film",
								email: "trainee@clapaction.com",
							},
						},
					},
					{
						title: "🎬 [APPEL À UN·E DIRECTEUR·RICE DE CASTING] ",
						description:
							"Je cherche aujourd’hui un·e directeur·rice de casting passionné·e, capable de m’accompagner dans la recherche de jeunes talents bruts, sensibles, instinctifs, pour incarner Lina, l’héroïne du film, mais aussi Noah, Salya, Mélanie, Hugo, et l’ensemble de son entourage scolaire et familial.\n\nJe ne cherche pas des visages connus, mais des jeunes qui sonnent juste, qui vibrent, qui portent en eux cette vérité fragile et puissante propre à l’adolescence. Je veux construire une galerie de personnages qui ne trichent pas.\n\n📩 Si ce projet vous intéresse,  je serais ravi d’échanger avec vous. \n\n📎 N’hésitez pas à me contacter \n\nMerci par avance pour votre attention, votre regard, et votre éventuelle collaboration.",
						buttons: {
							contact: {
								label: "Contactez l’équipe du film",
								email: "trainee@clapaction.com",
							},
						},
					},
					{
						title: "🎬 RECHERCHE CO-SCÉNARISTE",
						description:
							"Dans le cadre du développement de mon long-métrage *Traînée de Poudre*, je recherche un·e co-scénariste pour m’accompagner dans la réécriture du scénario.\n\nProjet ancré dans l’adolescence, les réseaux sociaux, le harcèlement et les relations toxiques. Sensibilité à ces sujets et goût pour la narration tendue et réaliste bienvenus.\n\n📩 Si vous êtes intéressé·e, merci d’envoyer un CV, un exemple d’écriture ou une courte note d’intention.",
						buttons: {
							contact: {
								label: "Contactez l’équipe du film",
								email: "trainee@clapaction.com",
							},
						},
					},
				],
				casting: {
					title: "Casting",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
			},
			friends: {
				page_title: "Celle qui n'avait pas vu Friends",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 10 mai 2024",
						},
						genre: {
							key: "Genre :",
							value: "Comédie absurde",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Film en financement",
				},
				project_needs: {
					label: "En recherche :",
					value: "Complément de financement",
				},
				content: {
					title: "Celle qui n'avait pas vu Friends",
					subtitle: "Quand tu te fais quitter le jour de ton mariage...",
					description:
						"Greg, 34 ans, retrouve sa future femme Rachel Karlson, 30 ans, quelques minutes avant la cérémonie. Elle est si belle… Il a envie de dire « oh-mon-Dieu!!!! » en référence à la série <i>Friends</i>. Rachel ne comprends pas sa blague... Et pour cause, elle n'a jamais vraiment regardé cette série, elle n'a jamais aimé. Greg est sous le choc, il ne veut plus se marier.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "cellequi@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action à venir",
					description:
						"Candidater pour l'un des derniers rôles principaux du film en nous envoyant vos essais. Nous vous communiquerons bientôt les infos pour y participer.",
				},
				casting: {
					title: "Casting confirmé",
					male: "Interprété par",
					female: "Interprétée par",
				},
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Charlotte Gabris",
					},
					referent_agent: {
						role: "Agent référent :",
						name: "Alexandra Schamis / AS TALENTS",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Charlotte Gabris"],
					},
					directors: {
						role: "Réalisateur(rice) :",
						names: ["Charlotte Gabris"],
					},
					producers: {
						role: "Producteur(s) :",
						names: ["Watch'Us Productions", "LaDCF (La Diversité du Cinéma)"],
					},
				},
			},
			lattente: {
				page_title: "L'attente",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 10 décembre 2024",
						},
						genre: {
							key: "Genre :",
							value: "Dramédie",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Début de l'écriture",
				},
				project_needs: {
					label: "En recherche :",
					value: "Co-autrice, réalisateur(rice), co-producteur, rôles principaux, financement",
				},
				content: {
					title: "L'attente",
					subtitle: "Dans l'attente, se cachent les réponses qu'on fuit",
					description:
						"Dans un monde où tout va trop vite, Serge Lassart, ancien ouvrier devenu visionnaire, imagine l'impensable : une immense salle d'attente universelle, un lieu où chacun est invité à faire une pause, à ralentir, et à réfléchir sur le sens de sa vie.<br /><br />De cet espace naît un mouvement collectif, bousculant les habitudes et révélant les destins croisés de personnages aussi différents qu'attachants.<br /><br />Des vies en suspens, des destins qui s'entrelacent : le narrateur, un homme mélancolique en quête de sens : les sœurs jumelles, mystérieuses voyageuses en quête de leurs origines ; Sandrine, un amour de jeunesse ; et April, une figure cynique de la société moderne, prisonnière d'un monde de performance et d'apparences.<br /><br />Avec une mise en scène chorale, L'Attente interroge notre rapport au temps, à la solitude et à la nécessité de se reconnecter à l'essentiel.<br /><br />À mesure que les trajectoires de ces personnages s'entrelacent, chaque attente révèle une vérité essentielle. Une tragédie bouleversante où le destin redistribue les cartes, questionnant notre besoin de reconnaissance et le véritable sens du bonheur.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "lattente@clapaction.com",
						},
					},
				},
				creative_source: {
					description: "D'après le roman  « L'ATTENTE » de Sébastien Praicheux.",
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				action: {
					title: "Action",
					description: "Si vous avez déjà écrit un scénario d'un projet film diffusé, candidatez pour collaborer à l'écriture / envoyez nous un mail.",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "lattente@clapaction.com",
						},
					},
				},
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Sébastien Praicheux",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Sébastien Praicheux"],
					},
					producers: {
						role: "Producteur(s) :",
						names: ["Watch'Us Productions"],
					},
				},
			},
			dim: {
				page_title: "DIM, L'histoire Vraie",
				header: {
					properties: {
						publication: {
							key: "Publication :",
							value: "le 5 février 2025",
						},
						genre: {
							key: "Genre :",
							value: "Biopic",
						},
						format: {
							key: "Format :",
							value: "Long-métrage",
						},
					},
				},
				production_stage: {
					label: "Stade de production :",
					value: "Synopsis",
				},
				project_needs: {
					label: "En recherche :",
					value: "Recherche du réalisateur et de co-producteurs",
				},
				content: {
					title: "DIM, L'histoire Vraie",
					subtitle: "L'homme au bas Nylon",
					description:
						"Entre biopic romantique et hommage vibrant, DIM - L'Histoire Vraie raconte la vie de Bernard Giberstein, sa face sombre et celle plus solaire : un visionnaire qui a révolutionné la place des femmes dans la société. Porté par son amour pour Sarah, sa muse et épouse, il transforme un simple collant en symbole de liberté et d'émancipation. Rescapé de la guerre, il offre aux femmes bien plus que des vêtements : la clé de leur autonomie, à l'image de cette première leçon de conduite qu'il a donnée à Sarah, déclarant (lui tendant les clés de sa voiture) : « La liberté, c'est avant tout savoir conduire. »…",
					buttons: {
						contact: {
							label: "Contacter l'équipe",
							email: "dimlefilm@clapaction.com",
						},
					},
				},
				tabs: {
					actions: "Actions",
					forum: "Forum",
					casting: "Casting",
					media: "Média",
					team: "Équipe",
				},
				actions: [
					{
						title: "🎬✨ #DIMLeFilmChallenge - VOTRE STYLE, NOTRE INSPIRATION !",
						description:
							"🔥 Et si votre tenue pouvait façonner l'univers visuel d'un film ? 🔥\n\nBernard Giberstein, le fondateur de Dim, a transformé un simple collant en un manifeste de liberté. Aujourd’hui, clapAction célèbre cette audace et s’inspire de VOUS pour capturer l’essence visuelle du film en préparation. 💫\n\n📸 LE CHALLENGE :\n\n1️⃣ Composez un look qui incarne la liberté, la confiance et l’élégance.\n\n2️⃣ Ajoutez une touche d’audace : un collant, des bas, un détail qui sublime votre silhouette et révèle votre féminité.\n\n3️⃣ Partagez votre photo en story ou en post, tagguez @‌clapaction et utilisez #DIMLeFilmChallenge. N’oubliez pas de mentionner 3 ami(e)s pour les inviter à jouer le jeu !\n\nOu envoyer nous directement par mail vos photos !\n\n✨ Bonus : Les looks les plus inspirants feront l’objet d’une exposition virtuelle sur Dim, L'histoire vraie et mettront à l’honneur l’élégance et la liberté d’aujourd’hui.",
						buttons: {
							contact: {
								label: "Contacter l'équipe",
								email: "dimlefilm@clapaction.com",
							},
						},
					},
					{
						title: "Action à venir",
						description: "Si vous êtes un réalisateur d'expériences et que le sujet vous intéresse, n'hésitez pas à nous contacter !",
						buttons: {
							contact: {
								label: "Contacter l'équipe",
								email: "dimlefilm@clapaction.com",
							},
						},
					},
					{
						title: "Action à venir",
						description:
							"Si vous avez des souvenirs d'une pub Dim qui vous a marqué ou que vous avez envie de nous partager, n'hésitez pas à nous en faire part. Nous la publierons ou la rechercherons pour vous !",
						buttons: {
							contact: {
								label: "Contacter l'équipe",
								email: "dimlefilm@clapaction.com",
							},
						},
					},
				],
				media: {
					title: "Photos & Vidéos",
				},
				team: {
					title: "Équipe",
					project_leader: {
						role: "Porteur de projet :",
						name: "Daniel Giberstein",
					},
					authors: {
						role: "Auteur(e)(s) :",
						names: ["Daniel Giberstein"],
					},
					producers: {
						role: "Producteur(s) :",
						names: ["Watch'Us Productions"],
					},
				},
			},
		},
		terms_of_use: {
			page_title: "Conditions d'utilisation",
			title: "Conditions d'utilisation",
			subtitle: "Politique de confidentialité",
			date: "Date de prise d'effet : 27/06/2024",
			watch_us: "WATCH'US (« nous », « notre », « nos ») exploite le site web https://www.techcannes.com/ et https://www.clapaction.com/",
			collect:
				"Cette page vous explique nos politiques en matière de collecte, d'utilisation et de communication des données à caractère personnel lorsque vous utilisez notre Site ainsi que les choix qui s'offrent à vous en ce qui concerne ces données.",
			use_data:
				"Nous utilisons vos données pour fournir et améliorer le Site. En utilisant le Site, vous consentez à la collecte et à l'utilisation d'informations conformément à la présente politique.",
			definitions: {
				title: "Définitions",
				site: "Site : Par Site on entend le site web https://www.techcannes.com/ et https://www.clapaction.com/ exploité par la société Watch'Us",
				items: [
					"Données à caractère personnel : Données à Caractère Personnel désigne des données concernant un individu vivant qui peut être identifié à partir de ces données (ou à partir de ces données et d'autres informations en notre possession ou susceptibles d'entrer en notre possession).",
					"Données d'Utilisation : Les Données d'Utilisation sont recueillies automatiquement et sont générées soit par l'utilisation du Site, soit par l'infrastructure du Site proprement dite (par exemple, durée de consultation d'une page).",
					"Cookies : Les cookies sont de petits fichiers enregistrés sur votre dispositif (ordinateur ou dispositif mobile).",
					"Responsable du Traitement : Responsable du Traitement désigne la personne physique ou morale (seule, ou conjointement ou en commun avec d'autres personnes) qui détermine les finalités et les modalités selon lesquelles toutes les données personnelles sont traitées ou doivent l'être. Aux fins de la présente Politique de Confidentialité, nous sommes un Responsable du Traitement de vos données à caractère personnel.",
					"Sous-Traitants (ou Prestataires de Services) : Le terme Sous-Traitant (ou Prestataire de Service) désigne toute personne physique ou morale qui traite les données au nom du Responsable du Traitement. Il se peut que nous fassions appel aux services de plusieurs Prestataires de Services afin de traiter vos données plus efficacement.",
					"Personne Concernée (ou Utilisateur) : Par Personne Concernée, on entend toute personne vivante qui utilise notre Site et est le sujet de Données à Caractère Personnel.",
				],
			},
			data_collection_and_use: {
				title: "Collecte et utilisation des données",
				subtitle: "Nous recueillons plusieurs types de données à différentes fins en vue de vous fournir notre Site et de l'améliorer.",
				types_of_data_collected: {
					title: "Types de données recueillies",
					personal_data: {
						title: "Données à Caractère Personnel",
						description:
							"Lorsque vous utilisez notre Site, il est possible que nous vous demandions de nous fournir certaines données personnelles nominatives qui peuvent servir à vous contacter ou à vous identifier (« Données à Caractère Personnel »). Les données personnelles nominatives peuvent comprendre, mais de manière non limitative :",
						items: ["Adresse e-mail", "Prénom et nom de famille", "Cookies et Données d'Utilisation"],
						use: "Nous utilisons vos Données à Caractère Personnel pour vous envoyer une réponse à votre demande effectuée via le formulaire de contact de notre site web. Nous nous engageons à ne pas vous envoyer de promotions et de démarchage commercial si vous ne nous donnez pas votre accord.",
					},
					usage_data: {
						title: "Données d'Utilisation",
						description:
							"Nous pouvons également recueillir des informations relatives au mode d'accès et d'utilisation du Site (« Données d'Utilisation »). Ces Données d'Utilisation peuvent comprendre des informations telles que l'adresse de protocole Internet (c.-à-d. l'adresse IP) de votre ordinateur, le type de navigateur, la version du navigateur, les pages de notre Site que vous consultez, la date et l'heure de votre visite, le temps passé sur ces pages, les identifiants uniques de dispositifs ainsi que d'autres données de diagnostic.",
					},
					tracking_cookies_data: {
						title: "Suivi et données de cookies",
						description:
							"Nous avons recours à des cookies et à d'autres technologies de suivi similaires pour effectuer un suivi des activités effectuées dans notre Site et nous conservons certaines informations.",
						definition:
							"Les cookies sont des fichiers à faible volume de données pouvant comporter un identifiant unique anonyme. Les cookies sont envoyés à votre navigateur depuis un site web et sont stockés sur votre dispositif.",
						consent: "Vous pouvez accepter ou refuser les Cookies directement via le panneau de gestion des Cookies en bas à droite du site web.",
					},
				},
				use_of_data: {
					title: "Utilisation des données",
					description: "La DCF utilise les données recueillies à des fins diverses :",
					items: [
						"Pour fournir et assurer notre Site",
						"Pour assurer assistance client",
						"Pour recueillir des données précieuses ou d'analyses qui nous permettront d'améliorer notre Site",
						"Pour contrôler l'utilisation de notre Site",
						"Pour détecter, prévenir et régler les problèmes techniques",
						"Pour répondre à toutes questions ou demandes de votre part via le formulaire de contact",
					],
				},
			},
			legal_basis: {
				title: "Fondement juridique du traitement des données à caractère personnel en vertu du Règlement Général relatif à la Protection des Données (RGPD)",
				description:
					"Si vous résidez dans l'Espace économique européen (EEE), le fondement juridique de La DCF en ce qui concerne la collecte et l'utilisation des données personnelles décrites dans la présente Politique de Confidentialité dépend des Données à Caractère Personnel que nous recueillons et du contexte précis dans lequel nous les recueillons.",
				legal_basis: [
					"Parce que vous nous avez autorisés à le faire",
					"Parce que nous avons un intérêt légitime à effectuer ce traitement et que vos droits ne priment pas sur cet intérêt légitime",
					"Pour respecter la loi",
				],
			},
			data_retention: {
				title: "Conservation des données",
				description:
					"Watch'Us ne conservera vos Données à Caractère Personnel que tant que cela sera nécessaire aux fins stipulées dans la présente Politique de Confidentialité. Nous conserverons et utiliserons vos Données à Caractère Personnel dans la mesure où cela sera nécessaire pour que nous puissions nous acquitter de nos obligations légales (par exemple pour respecter la législation en vigueur), résoudre des différends et appliquer nos accords et nos politiques. La durée légale de conservation de vos Données à Caractère Personnel est de 3 ans maximum.",
				usage_data_retention:
					"Watch'Us conservera également les Données d'Utilisation à des fins d'analyses internes. Les Données d'Utilisation sont généralement conservées pour une période plus courte sauf lorsque ces données sont utilisées pour renforcer la sécurité ou pour améliorer les fonctionnalités de notre Site ou si nous sommes légalement tenus de conserver ces données plus longtemps. La durée légale de conservation des Données d'Utilisation est de 13 mois maximum.",
			},
			data_transfer: {
				title: "Transfert des données",
				description:
					"Les informations vous concernant, notamment vos Données à Caractère Personnel, peuvent être transférées de votre région, province, pays ou autre division territoriale vers des ordinateurs – et stockées sur ces derniers – situés à un endroit où la législation relative à la protection des données diffère de celle du territoire où vous résidez.",
				international_transfer:
					"Si vous résidez hors de France et que vous choisissez de nous communiquer des informations, sachez que nous transférons les données, y compris les Données à Caractère Personnel, vers la France et que nous les y traitons. En acceptant la présente Politique de Confidentialité puis en soumettant ces informations, vous consentez à ce transfert.",
				security_measures:
					"Watch'Us prendra toutes les mesures raisonnablement nécessaires pour faire en sorte que vos données soient traitées de manière sécurisée et conformément à la présente Politique de Confidentialité, et vos Données à Caractère Personnel ne seront transférées vers aucune organisation ni aucun pays à moins que des contrôles adéquats ne soient en place, notamment en ce qui concerne la sécurité de vos données et d'autres données personnelles.",
			},
			data_disclosure: {
				title: "Communication de données",
				law_enforcement: {
					title: "Communication de données aux forces de l'ordre",
					description:
						"Dans certaines circonstances, Watch'Us peut être tenue de communiquer vos Données à Caractère Personnel si la loi l'exige ou en réponse à des demandes valides émanant de pouvoirs publics (par ex. un tribunal ou un organisme gouvernemental).",
				},
				legal_requirements: {
					title: "Exigences légales",
					description: "Watch'Us peut communiquer vos Données à Caractère Personnel si elle estime de bonne foi que cela est nécessaire pour :",
					items: [
						"S'acquitter d'une obligation légale",
						"Protéger et défendre les droits ou les biens de la DCF",
						"Prévenir d'éventuels actes répréhensibles ou enquêter sur de tels actes dans le cadre du Site",
						"Assurer la sécurité personnelle des utilisateurs du Site ou du public",
						"Se protéger contre la responsabilité civile",
					],
				},
			},
			data_security: {
				title: "Sécurité des données",
				description:
					"La sécurité de vos données nous tient à cœur. Toutefois, n'oubliez pas qu'aucune méthode de transmission de données par Internet ou méthode de stockage électronique n'est sûre à 100 %. Bien que nous nous efforcions d'utiliser des méthodes commercialement acceptables pour protéger vos Données à Caractère Personnel, nous ne pouvons pas leur garantir une sécurité absolue.",
			},
			user_rights: {
				title: "Droits à la protection des données qui vous sont accordés par le Règlement Général relatif à la Protection des Données (RGPD)",
				description:
					"Si vous résidez dans l'Espace économique européen (EEE), vous avez certains droits en matière de protection des données. La DCF entend prendre des mesures raisonnables pour vous permettre de corriger, de modifier ou de supprimer vos Données à Caractère Personnel ou d'en limiter l'utilisation.",
				contact_info:
					"Si vous souhaitez savoir quelles Données à Caractère Personnel nous détenons à votre sujet et voulez qu'elles soient supprimées de nos systèmes, veuillez nous contacter.",
				rights_list: [
					"Le droit d'accéder aux informations que nous détenons à votre sujet, de les actualiser ou de les supprimer. Lorsque cette option est proposée, vous pouvez consulter ou actualiser vos Données à Caractère Personnel ou en demander la suppression en nous contactant.",
					"Droit de rectification. Vous avez le droit de faire rectifier vos données si elles sont inexactes ou incomplètes.",
					"Droit d'opposition. Vous avez le droit de vous opposer à ce que nous traitions vos Données à Caractère Personnel.",
					"Droit de limitation. Vous avez le droit de nous demander de limiter le traitement de vos données personnelles.",
					"Le droit à la portabilité des données. Vous avez le droit de recevoir une copie des informations que nous détenons à votre sujet dans un format couramment utilisé, structuré et lisible par une machine.",
					"Droit au retrait du consentement. Vous avez également le droit de retirer votre consentement à tout moment si La DCF s'est appuyée sur votre consentement pour traiter vos données personnelles.",
				],
				proof_of_identity: "Veuillez noter que nous pouvons vous demander de prouver votre identité avant de répondre à des demandes de cette nature.",
				complaint:
					"Vous avez le droit de déposer plainte auprès d'une autorité de protection des données au sujet de notre collecte et de notre utilisation de vos Données à Caractère Personnel. Pour plus de précisions, veuillez contacter l'autorité de protection des données la plus proche de vous dans l'Espace économique européen (EEE).",
			},
			service_providers: {
				title: "Prestataires de services",
				description:
					"Nous pouvons faire appel à des sociétés tierces et à des tierces personnes pour faciliter la prestation de notre Site (« Prestataires de Services »), assurer le Site en notre nom, assurer des services liés au Site ou nous aider à analyser la façon dont notre Site est utilisé.",
				access_restriction:
					"Ces tiers n'ont accès à vos Données à Caractère Personnel que pour effectuer ces tâches en notre nom et il leur est interdit de les communiquer ou de les utiliser à quelle qu'autre fin.",
			},
			external_links: {
				title: "Liens pointant vers d'autres sites",
				description:
					"Il se peut que notre Site contienne des liens pointant vers d'autres sites que nous n'exploitons pas. Si vous cliquez sur un lien de tiers, vous serez redirigé vers le site de ce tiers. Nous vous recommandons vivement d'examiner la politique de confidentialité de chacun des sites que vous consultez. Nous n'avons aucun contrôle sur le contenu, les politiques ou pratiques de confidentialité des sites ou services de tiers et déclinons toute responsabilité en ce qui les concerne.",
			},
			policy_updates: {
				title: "Modifications de la présente Politique de Confidentialité",
				description:
					"Nous nous réservons le droit d'actualiser notre Politique de Confidentialité de temps à autre. Nous vous informerons de toute modification en publiant la nouvelle Politique de Confidentialité sur cette page.",
				notification:
					"Avant que la modification ne prenne effet, nous vous informerons en actualisant la « date de prise d'effet » qui figure en haut de la présente Politique de Confidentialité.",
				advice: "Nous vous conseillons de consulter la présente Politique de Confidentialité périodiquement pour prendre connaissance de toute modification. Les modifications apportées à la présente Politique de Confidentialité prennent effet lorsqu'elles sont publiées sur cette page.",
			},
			contact: {
				title: "Nous contacter",
				description:
					"Pour toute question relative à la présente Politique de Confidentialité ou pour toute demande concernant vos Données à Caractères Personnelles, veuillez nous contacter :",
				contact_info: {
					contact_person: "Contact DPO : Sarah Llouch",
					email: "Par courrier électronique : watchus13@gmail.com",
					address: "Par voie postale : 15 avenue Hoche, 75008 Paris",
				},
			},
			legal_notice: {
				title: "Mentions légales",
				description:
					"Ce site est créé par la société Watch'Us SARL au capital de 45 000€ (R.C.S. Paris B 482 201 407) sise au 15 AV HOCHE 75008 PARIS et présidée par Mme Sarah Lelouch. Adresse email : watchus13@gmail.com",
				publication_director: "Directeur de la publication : Mme Sarah Lelouch.",
				hosting_info: {
					company: "Le site est hébergé par OVH",
					details:
						"SAS au capital de 10 174 560 €\nRCS Lille Métropole 424 761 419 00045\nCode APE 2620Z\nN° TVA : FR 22 424 761 419\nSiège social : 2 rue Kellermann - 59100 Roubaix - France",
					subsidiary:
						"OVH SAS est une filiale de la société OVH Groupe SA société immatriculée au RCS de Lille sous le numéro 537 407 926 sise 2 rue Kellermann 59100 Roubaix.",
					publication_director: "Directeur de la publication : Michel Paulin",
				},
			},
		},
		privacy_policy: {
			page_title: "Politique de confidentialité",
			title: "Politique de confidentialité",
			subtitle: "Date d'entrée en vigueur : 19 avril 2024",
			foreword: {
				title: "Préambule",
				content:
					"La présente charte sur le respect de la vie privée formalise notre engagement concernant la protection de la vie privée des utilisateurs du site internet ClapAction exploité par Watch'US. Cette charte constitue le cadre contractuel entre l'utilisateur et Watch'US.",
			},
			data_protection: {
				title: "Respect des normes de protection des données",
				content:
					"Nous traitons vos données personnelles en stricte conformité avec le Règlement Général sur la Protection des Données (RGPD) du 27 avril 2016. Nous nous engageons à ne collecter que des données adéquates, pertinentes et limitées aux nécessités des services proposés, sans jamais inclure de données sensibles.",
			},
			collect: {
				title: "Collecte et utilisation des données personnelles",
				content:
					"Vos données sont collectées lors de la navigation sur le site, la création de compte, les transactions financières ou lorsque vous nous contactez. Ces données sont utilisées exclusivement pour la gestion et l'amélioration de nos services, ainsi que pour la communication relative à ces services.",
			},
			data_security: {
				title: "Conservation et sécurité des données",
				content:
					"Nous appliquons des mesures techniques et organisationnelles avancées pour garantir la sécurité et l'intégrité de vos données personnelles. Toutefois, nous rappelons que la sécurité absolue n'est pas garantie sur Internet. Il est de votre responsabilité de sécuriser également vos identifiants et mots de passe.",
			},
			personal_data: {
				title: "Partage des données personnelles",
				content:
					"Nous ne partageons vos données avec des tiers que pour les nécessités de gestion ou pour répondre à des obligations légales. Aucun transfert hors de l'Union Européenne n'est réalisé sans les garanties appropriées telles que les clauses contractuelles types de la Commission européenne.",
			},
			your_rights: {
				title: "Vos droits",
				content:
					"Vous disposez de droits étendus sur vos données personnelles, incluant l'accès, la correction, la suppression ou la limitation de leur traitement. Vous pouvez exercer ces droits en nous contactant directement par les moyens fournis dans cette charte.",
			},
			chart: {
				title: "Modifications de la charte",
				content:
					"Cette politique peut être mise à jour ; nous vous invitons à consulter régulièrement cette page pour vous tenir informé des changements qui pourraient affecter le traitement de vos données personnelles.",
			},
			contact: {
				title: "Contact et délégué à la protection des données",
				content:
					"Pour toutes questions ou pour exercer vos droits, vous pouvez nous contacter via l'email ou l'adresse postale indiquée. Si applicable, les coordonnées de notre Délégué à la Protection des Données sont également fournies.",
			},
			appeal: {
				title: "Recours",
				content: "En cas de litige, vous avez le droit de saisir la Commission Nationale de l'Informatique et des Libertés (CNIL).",
			},
		},
		profile: {
			page_title: "Profil",
			change_avatar: "Changer la photo de profil",
			cancel_button: "Annuler",
			submit_button: "Appliquer les modifications",
			update_success: "Votre modification a été prise en compte.",
			inputs: {
				firstname: {
					label: "Prénom",
					placeholder: "Jhon",
				},
				lastname: {
					label: "Nom",
					placeholder: "Doe",
				},
				email: {
					label: "Email",
					placeholder: "jhondoe@gmail.com",
				},
				phone: {
					label: "Téléphone",
					placeholder: "06 18 24 32 17",
				},
				birthdate: {
					label: "Date de naissance",
					placeholder: "24/05/1996",
				},
				city: {
					label: "Ville",
					placeholder: "Paris",
				},
				address: {
					label: "Adresse",
					placeholder: "18 rue Maréchal-Juin",
				},
				zipCode: {
					label: "Code postal",
					placeholder: "75 001",
				},
				sex: {
					label: "Sexe",
					options: [
						{
							label: "Homme",
							value: "homme",
						},
						{
							label: "Femme",
							value: "femme",
						},
						{
							label: "Autre",
							value: "autre",
						},
					],
				},
				password: {
					label: "Mot de passe",
					placeholder: "johndoemotdepasse@78",
					additional_info: "Au moins 8 caractères dont 1 majuscule, 1 minuscule et 1 chiffre.",
				},
				lang: {
					label: "Langue du compte",
					options: [
						{
							value: "fr",
							label: "Français",
						},
						{
							value: "en",
							label: "English",
						},
					],
				},
			},
		},
		home: {
			page_title: "Accueil",
			banner: {
				title: "Vos idées prennent vie sur",
				inside_title: "grand écran !",
				subtitle: "Protégez vos idées, participez, et produisez le cinéma de demain !",
				firstContent: [
					{
						text: "Vous pourrez bientôt acquérir des objets uniques et certifiés, directement liés à des films en tournage ou déjà sortis en salle, diffusés à la télé ou sur des plateformes. Pour les films à venir, ces pièces exclusives seront proposées aux enchères, tandis que pour les films déjà exploités, elles seront disponibles à prix fixe. Une opportunité inédite de vous offrir des décors, costumes, affiches dédicacées, ou tout autre élément iconique du 7ᵉ art.",
					},
					{
						text: "Pour les professionnels du cinéma, mettre en vente ces objets n'est pas seulement un moyen de partager des morceaux d'histoire avec le public, c'est aussi une manière innovante de financer leurs projets en développement.",
					},
					{
						text: "Grâce à ClapAction, ces objets sont associés à un NFT garantissant leur authenticité et leur traçabilité, renforçant ainsi leur valeur auprès des collectionneurs et des cinéphiles. C'est une manière innovante et concrète de donner une seconde vie aux objets tout en contribuant à écrire l'avenir du cinéma.",
					},
					{
						text: "La valeur de ces objets ne cessera de croître avec le temps, en fonction du succès des films, de leur impact culturel ou des carrières des artistes associés. Posséder un costume, un décor ou une affiche d'un film culte ou d'un acteur devenu légendaire, c'est non seulement acquérir une œuvre intemporelle, mais aussi investir dans un patrimoine à forte valeur émotionnelle et financière.",
					},
					{
						text: "Avec clapAction, vous avez accès à la toute première Marketplace au monde dédiée à la vente d'objets de cinéma, de manière fiable, sécurisée, et avec une garantie d'authenticité. Bien plus qu'un simple achat, c'est une immersion dans la création cinématographique et un soutien direct aux artistes de demain.",
					},
					{
						text: "Si vous êtes un professionnel et souhaitez faire parti des premiers à mettre en vente l'un de vos objets lié à l'une de vos productions dès le mois de février prochain, contactez-nous : contact@clapaction.com",
					},
				],
				discover_project: "Découvrir les projets",
				submit_project: "Déposer un projet",
			},
			why_join: {
				title: "Pourquoi rejoindre",
				inside_title: "clapAction ?",
				content:
					"Bienvenue sur clapAction, ici vos idées prennent vie grâce à la puissance de l'IA et à la sécurité inégalée de la blockchain. Que vous soyez un créateur passionné ou un fervent supporter de projets innovants, clapAction vous offre une expérience unique et sécurisée.",
				button: "Créer un compte",
				creator_title: "Créateurs",
				creator_content: "Protégez vos idées grâce à la technologie blockchain et, si vous le souhaitez, partagez-les avec des professionnels du cinéma.",
				public_title: "Public",
				public_content: "Exprimez votre soutien en votant pour les projets qui vous inspirent.",
			},
			marketplace: {
				title: "Bientôt : achetez parmi une collection authentique",
				inside_title: "d'objets cinématographiques certifiés",
				subtitle: "Possédez une part de cinéma, soutenez la création",
				firstContent: [
					{
						text: "Vous pourrez bientôt acquérir des objets uniques et certifiés, directement liés à des films en tournage ou déjà sortis en salle, diffusés à la télé ou sur des plateformes. Pour les films à venir, ces pièces exclusives seront proposées aux enchères, tandis que pour les films déjà exploités, elles seront disponibles à prix fixe. Une opportunité inédite de vous offrir des décors, costumes, affiches dédicacées, ou tout autre élément iconique du 7ᵉ art.",
					},
					{
						text: "Pour les professionnels du cinéma, mettre en vente ces objets n'est pas seulement un moyen de partager des morceaux d'histoire avec le public, c'est aussi une manière innovante de financer leurs projets en développement.",
					},
					{
						text: "Grâce à ClapAction, ces objets sont associés à un NFT garantissant leur authenticité et leur traçabilité, renforçant ainsi leur valeur auprès des collectionneurs et des cinéphiles. C'est une manière innovante et concrète de donner une seconde vie aux objets tout en contribuant à écrire l'avenir du cinéma.",
					},
					{
						text: "La valeur de ces objets ne cessera de croître avec le temps, en fonction du succès des films, de leur impact culturel ou des carrières des artistes associés. Posséder un costume, un décor ou une affiche d'un film culte ou d'un acteur devenu légendaire, c'est non seulement acquérir une œuvre intemporelle, mais aussi investir dans un patrimoine à forte valeur émotionnelle et financière.",
					},
					{
						text: "Avec clapAction, vous avez accès à la toute première Marketplace au monde dédiée à la vente d'objets de cinéma, de manière fiable, sécurisée, et avec une garantie d'authenticité. Bien plus qu'un simple achat, c'est une immersion dans la création cinématographique et un soutien direct aux artistes de demain.",
					},
					{
						text: "Si vous êtes un professionnel et souhaitez faire parti des premiers à mettre en vente l'un de vos objets lié à l'une de vos productions dès le mois de février prochain, contactez-nous : contact@clapaction.com",
					},
				],
				explore_title: "Explorez notre boutique",
				explore_content: "Plongez dans notre marketplace dédiée aux passionnés de cinéma.",
				certified_title: "Objets certifiés",
				certified_content: "Une large sélection d'objets authentiques et certifiés sur la blockchain.",
			},
			featured_projects: {
				title: "Projets en",
				inside_title: "développement",
				subtitle: "Explorez les récits captivants et les univers uniques des projets accompagnés par clapAction.",
				discover_project: "Découvrir les projets",
			},
			storytelling: {
				first_block: {
					title: "Passionnés : Participez à la",
					inside_title: "création",
					title_2: "et à la",
					inside_title_2: "production des films",
					subtitle:
						"Votez pour contribuer étape par étape à la production des films : du développement (écriture, casting...) à la sortie en salles, en passant par le tournage et la post-production. Interagissez avec la communauté, et bénéficiez d'avantages exclusifs : accès à des avant-premières, à des tournages, et à vos artistes préférés.",
				},
				second_block: {
					title: "Partagez vos idées",
					inside_title: "en toute sécurité",
					subtitle:
						"Vos documents sont ancrés sur la blockchain, ce qui garantit la préservation et la sécurité intégrale de votre création. Que vous soyez professionnels ou amateurs, partagez vos idées avec l'assurance  qu'elles seront soigneusement protégées à vie.",
					additionnal_subtitle: "NB : vous pouvez choisir de protéger vos idées et de les garder confidentielles, ou de les soumettre à notre comité de sélection.",
				},
				third_block: {
					title: "Professionnels : Consultez",
					inside_title: "l'avis du public",
					title_2: "à tout moment",
					subtitle:
						"Créez une communauté autour de vos projets, accédez à un baromètre d'opinion, soumettez vos productions aux votes du public pour vous orienter dans votre développement.",
				},
			},
			achivement: {
				title: "Notre",
				inside_title: "réalisation phare",
				subtitle: "Celle qui n'avait pas vu Friends",
				content:
					"Greg retrouve sa future femme Rachel quelques minutes avant la cérémonie. Elle est si belle, il a envie de dire « Oh mon Dieu ! » en référence à un personnage dans la série Friends. Rachel ne comprend pas sa blague, elle n'a jamais vu la série. Greg est choqué, il ne veut plus se marier.",
			},
			FAQ: {
				title: "Questions fréquemment posées",
				questions: [
					{
						title: "Comment puis-je déposer une idée de film, un synopsis ou un scénario ?",
						content: "Créez un compte, remplissez le formulaire de dépôt et téléchargez votre PDF, si vous en avez un.",
					},
					{
						title: "Quels sont les critères de sélection des projets ?",
						content: "Les projets sont sélectionnés sur la base de leur originalité, de leur faisabilité et de leur potentiel d'exploitation.",
					},
					{
						title: "Comment est étudié mon projet lorsqu'il est déposé et que vous souhaitez prétendre à une aide au développement ?",
						content:
							"Votre projet est d'abord étudié par le Comité de Lecture interne de LaDCF. S'il est retenu, il est ensuite présenté au Comité de Sélection (composé de 10 personnalités reconnues) qui décide des films dont le développement sera financé et encadré par des professionnels. Découvrez le Comité de Sélection de LaDCF.",
					},
					{
						title: "Comment s'affiche les résultats du Comité de Lecture ?",
						content:
							"Vous pouvez suivre l'avancée du statut de votre projet dans la page « mes projets », grâce à trois icônes : « retenu », « non-retenu », ou « en cours d'étude ».",
					},
					{
						title: "Quel est le délai de réponse du Comité de Lecture ?",
						content:
							"Il n'y a pas de délai spécifié par le Comité de Lecture. En moyenne, un projet met 1 mois à être étudié, mais ce temps peut varier selon la quantité de projets reçus chaque semaine. Pour information le Comité de Sélection se réunit chaque trimestre.",
					},
					{
						title: "Comment puis-je voter pour un projet ?",
						content: "Connectez-vous à votre compte, naviguez vers la page des projets et votez via les options disponibles.",
					},
					{
						title: "Le nombre de dépôts de projet est-il limité ?",
						content:
							"Le nombre de projets que vous pouvez déposer sur la plateforme n'est pas limité, vous pouvez déposer autant de projets que vous le souhaitez et bénéficier d'une authentification sur la blockchain. Tous les dépôts de projets ont une valeur juridique.",
					},
					{
						title: "Peut-on modifier ou supprimer un projet déposé ?",
						content:
							"Tant que le projet n'est pas ancré sur la blockchain, le projet est modifiable. Une fois ancré, il sera inscrit à tout jamais sur la blockchain et par conséquent, il ne sera plus modifiable ou supprimable.",
					},
					{
						title: "Puis-je déposer une vidéo avec mon projet ?",
						content:
							"Vous ne pouvez pas déposer de vidéos directement sur la plateforme, mais vous pouvez inclure un lien renvoyant vers la vidéo dans votre Synopsis ou votre CV.",
					},
					{
						title: "La plateforme aide-t-elle des particuliers à trouver un producteur ?",
						content:
							"Si vous déposez un projet en tant que particulier, il sera automatiquement étudié par le Comité de LaDCF. Si vous êtes un auteur, réalisateur ou producteur, vous faites parti de la catégorie des professionnels. En publiant votre projet sur la plateforme vous pouvez trouver des producteurs, diffuseurs, distributeurs, via l'espace professionnel, interagir avec le public et créer une communauté autour de votre projet et ce dès le stade du développement.",
					},
				],
			},
		},
		confirmation_code: {
			page_title: "Confirmation du code",
			title: "Code d'authentification",
			subtitle: "Entrez le code OTP que nous venons de vous envoyer sur votre adresse e-mail enregistré",
			button: "Se connecter",
			send_code: "Envoyer un nouveau code",
		},
		my_projects: {
			page_title: "Mes projets déposés",
			title: "Mes projets déposés",
			dashboard_header: {
				all: "Tous les projets",
				online: "En ligne",
				draft: "Draft",
				archived: "Archivés",
				create_project: "Créer un nouveau projet",
			},
			filters: {
				all_projects: "Tous les projets",
				online: "En ligne",
				draft: "Draft",
				archived: "Archivé",
				accepted: "Retenu",
				refused: "Non retenu",
				studying: "En cours d'étude",
				anchored: "Ancré",
				not_anchored: "En attente d'ancrage",
				anchor_failed: "Ancrage échoué",
			},
			table: {
				headers: {
					project_name: "Nom du projet",
					project_leader: "Porteur du projet",
					author: "Auteur",
					ai_image: "Image IA",
					anchor_proof: "Certificat",
					status: "Status",
					date: "Date",
				},
				waiting: "En attente...",
				see_image: "Voir l'image",
				anchor_link: "Lien d'ancrage",
				download: "Télécharger",
				anchor: "Ancrer",
				menu: {
					archive: "Archiver",
					restore: "Restaurer",
					publish: "Publier",
					anchor: "Ancrer le projet",
					download_proof: "Télécharger la preuve d'ancrage",
					data: "Informations sur le projet",
					edit: "Modifier le projet",
				},
			},
			drawer: {
				button: {
					see_project: "Voir le projet",
					anchor: "Ancrer",
					modify: "Modifier mon projet",
					modify_socials: "Modifier les liens",
					download_proof: "Télécharger le certificat",
					follow_us: "Suivez nous sur :",
					archive_project: "Archiver",
					edit_project: "Modifier le projet",
					publish: "Publier",
				},
				modal: {
					title: "Êtes-vous sûr de vouloir ancrer ce projet ?",
					content1: "L'ancrage est une action permanente qui verrouille votre projet dans son état actuel.",
					content2: "Une fois que vous avez ancré le projet, vous ne pourrez plus modifier son contenu.",
					content3: "Cette opération est irréversible et garantit que les données ne pourront pas être altérées par la suite.",
					content4: "Veuillez vous assurer que toutes les modifications nécessaires ont été effectuées avant de confirmer cette action.",
					button: {
						confirm: "Ancrer",
						cancel: "Annuler",
					},
				},
			},
		},
		submit_project: {
			page_title: "Déposer un projet",
			title: "Déposer un projet",
			subtitle: "protégé, sécurisé, et certifié",
			description: {
				items: [
					{
						text: "Partagez vos idées en toute sérénité en remplissant les champs ci-dessous.",
					},
					{
						text: "Grâce à notre technologie, chaque projet déposé est automatiquement <b>ancré sur la blockchain</b>. Une fois votre projet ancré, vous recevrez un certificat d'authenticité pour garantir la protection de votre création, quelle que soit la raison de votre dépôt.",
					},
					{
						text: "De plus, une affiche de film générée par notre Intelligence Artificielle vous sera proposée en exclusivité. Un outil inspirant, conçu pour donner vie à votre vision.",
					},
					{
						text: "Votre projet reste <b>strictement confidentiel</b> : aucune information ne sera partagée sans votre accord.",
					},
				],
			},
			labels: {
				project_title: "Titre du projet",
				project_title_placeholder: "100 caractères maximum",
				project_pitch: "Pitch",
				project_format: "Format",
				project_reason: "Quelle est la raison principale pour laquelle vous déposez votre projet ?",
				project_pitch_additionnal:
					"Veuillez fournir une description concise et attrayante. Cette description sera utilisée pour mettre en avant votre projet sur la plateforme.",
				project_pitch_placeholder: "Pitch (150 caractères minimum)",
				project_linkedin: "Veuillez fournir un lien LinkedIn si vous ne soumettez pas votre CV",
				cv: "CV",
				scenario: "Synopsis et/ou éléments artistiques",
			},
			selections: {
				format: {
					selectAnOption: "Sélectionner un format",
					long_metrage: "Long métrage",
					court_metrage: "Court métrage",
					serie: "Série",
					documentaire: "Documentaire",
				},
				reason: {
					protect: "Je dépose mon projet simplement pour le protéger",
					protect_and_development_aid: "Je dépose mon projet pour le protéger et le soumettre à l'aide au développement",
				},
				purposes: {
					funding: "Soumettre mon projet au comité de sélection de LaDCF (La Diversité du Cinéma Français)",
					funding_comment: "(Pour prétendre à une aide au développement)",
					protect: "Obtenir un certificat d'authenticité et protéger mon idée",
					pitch: "Participer à une sélection pour présenter mon projet à des professionnels avec Neeedco",
				},
			},
			drag_and_drop: {
				cv: "Glisser ou cliquer ici pour télécharger le CV",
				scenario: "Glisser ou cliquer ici pour télécharger votre document",
			},
			confidentiality: {
				scenarioProtection: {
					text: "NB : En déposant votre projet sur clapAction, les éléments de votre projet (scénario, synopsis, dossier, etc.) seront protégés sur la blockchain. Si vous souhaitez que votre projet soit étudié par le Comité de Sélection, nous vous invitons à limiter votre présentation à 2 pages dactylographiées.",
				},
				projectSharing: {
					text: "Si vous souhaitez déposer votre projet afin de le partager publiquement avec la communauté, rdv sur",
					suscribePageLink: "l'espace dédié au professionnels.",
				},
			},
			send_button: "Déposer le projet",
		},
		edit_project: {
			page_title: "Modifier le projet",
			title: "Modifier le projet",
			subtitle: "Modifiez les informations de votre projet en remplissant les champs ci-dessous.",
			labels: {
				project_title: "Titre du projet",
				project_pitch: "Pitch",
				project_format: "Format",
				project_reason: "Quelle est la raison principale pour laquelle vous déposez votre projet ?",
				project_pitch_additionnal:
					"Veuillez fournir une description concise et attrayante. Cette description sera utilisée pour mettre en avant votre projet sur la plateforme.",
				project_pitch_placeholder: "Pitch (150 caractères minimum)",
				project_linkedin: "Veuillez fournir un lien LinkedIn si vous ne soumettez pas votre CV",
				cv: "CV",
				scenario: "Synopsis et/ou éléments artistiques",
				purpose: "Pourquoi déposez-vous votre projet ? (Vous pouvez cocher plusieurs options)",
			},
			selections: {
				format: {
					long_metrage: "Long métrage",
					court_metrage: "Court métrage",
					serie: "Série",
					documentaire: "Documentaire",
				},
				reason: {
					protect: "Je dépose mon projet simplement pour le protéger",
					protect_and_development_aid: "Je dépose mon projet pour le protéger et le soumettre à l'aide au développement",
				},
			},
			drag_and_drop: {
				cv: "Glisser ou cliquer ici pour télécharger le CV",
				scenario: "Glisser ou cliquer ici pour télécharger votre document",
			},
			confidentiality:
				"NB : En déposant votre projet sur clapAction, le scénario sera protégé sur la blockchain. Si vous souhaitez que votre projet soit étudié par le Comité de Sélection, nous vous invitons à ne pas dépasser les 2 pages dactylographiées.",
			send_button: "Modifier le projet",
		},
		contact: {
			page_title: "Contact",
			title: "Contactez-nous",
			subtitle: "Besoin d'un renseignement ? Envoyez-nous vos questions ou commentaires, nous vous répondrons rapidement !",
			labels: {
				subject: "Sujet",
				email: "Email",
				message: "Message",
				firstname: "Prénom",
				lastname: "Nom",
				phone: "Téléphone",
			},
			confirmation: "Merci pour votre message, nous vous répondrons dans les plus brefs délais.",
			button: "Envoyer",
		},
		subscribe: {
			page_title: "Abonnez-vous",
			title: "Aller chercher votre public, apprenez à le connaître, découvrez ses attentes et offrez une",
			inside_title: "communication impactante et inédites à vos projets !",
			box: "Vous êtes un professionnel : auteur, réalisateur, producteur, diffuseur ou distributeur ?",
			subtitle: "Obtenez des insights précieux sur votre public et influencez l'industrie",
			description: {
				title: "Vous développez des films\u00A0?",
				content1: "Avec clapAction créez une communauté autour de vos projets et donnez-leur de la notoriété, bien avant sa sortie !",
				content2:
					"Partagez vos films à n'importe quelle étape de sa production : de l'idée à sa sortie. Ainsi, découvrez les attentes du public, impliquez-le dans votre processus de développement et de création, consultez ses datas et laissez votre communauté faire le bouche-à-oreille dès la naissance de votre projet.",
			},
			button_box: {
				title: "Abonnez-vous dès maintenant !",
				content1: "Dès le mois de juin prochain, vous pourrez partager en exclusivité, les projets qui vous animent aujourd'hui pour en faire les succès de demain.",
				content2: "L'attente est l'écrin du bonheur… clapAction en est un.",
				button: "Abonnez-vous",
			},
			abilities_title: "Tous les professionnels pourront :",
			deposit:
				"Déposer leur(s) film(s) sur la plateforme à n'importe quel stade de sa production (du développement à sa sortie) afin de créer une communauté et la consulter dès qu'ils le souhaitent",
			federate: "Fédérer et engager une communauté autour de chaque projet en commençant par profiter de toute la communauté de clapAction.",
			contribute:
				"Solliciter le public pour n'importe quel sujet/question (votes, recherches d'idées, appel à témoignages en lien avec leur sujet, aide au casting, choix du titre, affiches, musiques etc…)",
			use_data: "Avoir accès à l'ensemble des datas liées à leur projet.",
			deposit_content: "Déposer et partager les contenus de leur choix liés à leur(s) projets(s) : vidéos, coulisses, itv, reels etc…",
			offer: "Offrir, si le projet en a déjà, des liens directs à leurs réseaux sociaux.",
			drive: "Accompagner la création de chaque communauté, de façon singulière et adaptée à leur sujet.",
			shape: "Façonner leurs projets en fonction de l'attente du public",
			types: {
				title: "Types :",
				type1: "Genre (Homme/femme)",
				type2: "Tranche d'âge",
				type3: "Lieux de résidence",
				type4: "Genre de films qu'ils aiment",
				type5: "Contenus crées par leur communauté, liés à leur projet",
				type6: "Pourcentage d'intérêt pour leur projet par rapport à l'ensemble des films sur clapAction",
			},
			contact: "Nous contacter",
			radio: {
				title: "Vous souhaitez",
				options: {
					build: "Partager un film, construire et interagir avec sa communauté et apprendre à la connaître",
					access: "Partager plusieurs films et avoir accès à toutes les données et à toutes les attentes du public",
				},
			},
			labels: {
				firstname: "Prénom",
				lastname: "Nom",
				address: "Adresse",
				phone: "Téléphone",
				email: "Email",
				message: "Vous avez une question, envoyez-nous un message",
			},
			confirmation: "Merci pour votre message, nous vous répondrons dans les plus brefs délais.",
			button: "Envoyer",
		},
		dashboard: {
			page_title: "Tableau de bord",
		},
		reset_password: {
			page_title: "Réinitialisation du mot de passe",
			title: {
				first: "Réinitialisation du",
				second: "mot de passe",
			},
			subtitle: "Veuillez saisir un nouveau mot de passe",
			labels: {
				password: "Mot de passe",
				password_additionnal: "Au moins 8 caractères",
				password_confirm: "Confirmer le mot de passe",
			},
			reset_button: "Changer de mot de passe",
		},
		legal_notice: {
			page_title: "Mentions légales",
			title: "Mentions légales",
			subtitle:
				"Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance en l'économie numérique, il est précisé aux utilisateurs du site clapAction l'identité des différents intervenants dans le cadre de sa réalisation et de son suivi.",
			edition: {
				title: "Edition du site",
				url: "Le présent site, accessible à l'URL https://clapaction.app/ (le « Site »), est édité par :",
				company:
					"CLAPACTION, société au capital de 1000 euros, inscrite au R.C.S. de PARIS sous le numéro 934 285 610 R.C.S Paris, dont le siège social est situé au 28 rue de Turin 75008 Paris, représenté(e) par Sarah Lelouch dûment habilité(e)",
				tva: "Le numéro individuel TVA de l'éditeur est : FR35934285610.",
			},
			hosting: {
				title: "Hébergement",
				hosted: "Le Site est hébergé par la société OVH SAS, situé 2 rue Kellermann - BP 80157 - 59053 Roubaix Cedex 1, (contact téléphonique ou email : 1007).",
			},
			publication: {
				title: "Directeur de publication",
				content: "Le Directeur de la publication du Site est Sarah Lelouch.",
			},
			contact: {
				title: "Nous contacter",
				mail: "Par email : contact@clapaction.com",
				letter: "Par courrier : 15 AVENUE HOCHE, 75008 PARIS",
				legal: "Génération des mentions légales par Legalstart",
			},
		},
		forgot_password: {
			page_title: "Mot de passe oublié",
			title: "Mot de passe oublié ?",
			labels: {
				email: "Email",
				email_additionnal: "*Nous allons envoyer un lien par mail pour réinitialiser votre mot de passe",
			},
			send_button: "Envoyer le lien par mail",
			redirect_link: "Retour",
			confirmation: "Un email vous a été envoyé avec un code de réinitialisation de Password.",
		},
		login: {
			page_title: "Se connecter",
			title: "Se connecter",
			subtitle: "",
			labels: {
				email: "Email",
				password: "Mot de passe",
			},
			password_forgotten: "Mot de passe oublié ?",
			login_button: "Se connecter",
			redirect_text: "Vous n'avez pas de compte ?",
			redirect_link: "S'inscrire",
		},
		projects: {
			page_title: "Les projets",
			title: "Découvrez les films accompagnés par",
			inside_title: "clapAction",
			subtitle: "L'algorithme ? C'est vous. Les films défilent selon vos coups de cœur.",
			filter_button: "Filtres",
			filter: {
				title: "Filtres",
				format_label: "Format",
				format_placeholder: "Sélectionner votre format",
				sort_label: "Trier par",
				sort_placeholder: "Sélectionner votre choix de tri",
				theme_label: "Genre",
				theme_placeholder: "Sélectionner le genre correspondant",
				clear_all: "Tout effacer",
				show_projects: "Afficher les {count} projets",
			},
			tag: {
				format: {
					long_metrage: "Long-métrage",
					court_metrage: "Court-métrage",
					serie: "Série",
					documentaire: "Documentaire",
				},
				theme: {
					comedie: "Comédie",
					comedie_romantique: "Comédie romantique",
					comedie_dramatique: "Comédie dramatique",
					biopic: "Biopic",
					thriller: "Thriller",
					film_daction: "Film d'action",
					film_policier: "Film policier",
					film_dhorreur: "Film d'horreur",
					film_historique: "Film historique",
					film_aventure: "Film d'aventure",
					film_documentaire: "Film documentaire",
					science_fiction: "Science-fiction",
					film_danimation: "Film d'animation",
					comedie_musicale: "Comédie musicale",
					dramedie: "Dramédie",
					comedie_absurde: "Comédie absurde",
					comedie_sociale: "Comédie sociale",
					thriller_psychologique: "Thriller psychologique",
				},
				sort: {
					newest: "Les plus récents",
					oldest: "Les plus anciens",
					most_liked: "Les plus aimés",
					least_liked: "Les moins aimés",
				},
			},
		},
		complete_profile: {
			page_title: "Complétez votre profil",
			title: "Avant de déposer un projet complétez votre profil",
			labels: {
				firstname: "Prénom",
				lastname: "Nom",
				pseudo: "Pseudo",
				email: "Email",
				password: "Mot de passe",
				password_confirmation: "Confirmer le mot de passe",
				password_additionnal: "Au moins 8 caractères",
				checkbox: "J'accepte les conditions d'utilisation",
				zipcode: "Code postal",
				city: "Ville",
				address: "Adresse",
				phoneNumber: "Téléphone",
			},
			next_button: "Suivant",
		},
		register: {
			page_title: "S'incrire",
			title: "S'inscrire",
			subtitle:
				"Rejoignez clapAction : proposez vos idées de films ou de séries en toute confiance, et participez à la création des projets de clapAction. Tous les champs ci-dessous sont obligatoires.",
			inputs: {
				firstname: {
					label: "Prénom",
				},
				lastname: {
					label: "Nom",
				},
				email: {
					label: "Email",
				},
				password: {
					label: "Mot de passe",
					additionnal_info: "Au moins 8 caractères",
				},
				confirm_password: {
					label: "Confirmer le mot de passe",
				},
				lang: {
					label: "Langue du compte",
					options: [
						{
							value: "fr",
							label: "Français",
						},
						{
							value: "en",
							label: "English",
						},
					],
				},
				terms_of_use: {
					label: "J'accepte les conditions d'utilisation",
				},
			},
			register_button: "S'inscrire",
			redirect_text: "Vous avez déjà un compte ?",
			redirect_link: "Se connecter",
		},
		post: {
			return: "Retour",
			title: "Forum",
			comment: {
				submit: "Publier",
				placeholder: "Partagez votre avis...",
				default_subtitle: "Échangeons ensemble",
				empty: "Aucun commentaire",
				share: "Soyez le premier à partager votre avis !",
				reply: {
					delete: "Supprimer",
					submit: "Répondre",
					no_reply: "{{n}} réponse",
					single_reply: "{{n}} réponse",
					many_replies: "{{n}} réponses",
					your_reply: "Votre réponse...",
					see_more: "Voir plus",
				},
			},
		},
		prix_alice_guy: {
			page_title: "Prix Alice Guy",
			header: {
				publication: "Publication: du 1er décembre 2024 au 31 janvier 2025",
				genre: "Genre: Films de réalisatrice",
				format: "Format: Vote",
			},
			content: {
				title: "Votez pour le Prix Alice Guy 2025",
				description: `Le Prix Alice Guy récompense, depuis 2018, le meilleur film français réalisé par une femme et sorti au cinéma dans l'année. Tout le monde peut contribuer à le choisir. Comment ? En votant pour vos 5 films préférés dans la liste ci-dessous.`,
				sub_description: "Vous avez jusqu'au 31 janvier 2025 pour faire votre choix.",
			},
			buttons: {
				vote: "Participer au vote",
				discover: "Découvrir la page Alice Guy",
			},
			sections: {
				first: {
					title: "Pour voter, rien de plus simple.",
					description: `Tous les films éligibles (ils sont <strong>85 cette année</strong>) figurent dans la liste fournie. Cliquez sur  “Je vote” et cochez 3 films au minimum et 5 au maximum pour que votre vote soit enregistré. Tout le monde peut voter… mais chacun.e ne peut voter qu'une seule fois. <br/>Pour valider votre choix, il suffit de laisser votre adresse email. <br/><br/><strong>L'urne virtuelle se refermera le 31 janvier 2025 à minuit.</strong>`,
				},
				second: {
					title: "Et après?",
					list: {
						first: "Le 1er février 2025, les 5 films qui auront reçu le plus voix (1 vote = 1 voix) seront annoncés. Fin février, ils seront départagés par un jury paritaire de 6 professionnels du cinéma.",
						second: "Une soirée de remise du Prix Alice Guy sera ensuite organisée à Paris pour honorer la lauréate, la mémoire et le travail d'Alice Guy, première réalisatrice au monde et les 130 ans du cinéma. Et comme chaque année, la soirée sera ouverte à tout.es.",
					},
				},
			},
		},
	},
	component: {
		project_card: {
			project_leader: "Porteur de projet :",
		},
		create_project_modal: {
			title: "Création d'un projet",
			buttons: {
				submit: "Créer mon projet",
				cancel: "Annuler",
			},
		},
		theme_selector: {
			title: "Genre",
			select: "Sélectionner le genre correspondant",
			themes: {
				comedie: "Comédie",
				comedie_romantique: "Comédie romantique",
				comedie_dramatique: "Comédie dramatique",
				biopic: "Biopic",
				thriller: "Thriller",
				film_daction: "Film d'action",
				film_policier: "Film policier",
				film_dhorreur: "Film d'horreur",
				film_historique: "Film historique",
				science_fiction: "Science fiction",
				film_danimation: "Film d'animation",
				comedie_musicale: "Comédie musicale",
				dramédie: "Dramédie",
				comedie_absurde: "Comédie absurde",
				thriller_psychologique: "Thriller psychologique",
				comedie_sociale: "Comédie sociale",
				film_aventure: "Film d'aventure",
				film_documentaire: "Film documentaire",
			},
		},
		featured_project: {
			project_leader: "Porteur de projet :",
			published_at: "Publication : le {{var1}}",
			category: "Genre :",
			length: "Format :",
			button: "Voir le projet",
		},
		drag_and_drop_file: {
			no_file_selected: "Aucun fichier sélectionné",
			file_too_big: "Le fichier dépasse la taille maximale de {{maxSize}}MB",
		},
		authpagetemplate: {
			footer: {
				copyright: "© 2024 clapAction",
			},
		},
		lang_selector: {
			fr: "FR",
			en: "EN",
		},
		header: {
			login: "Se connecter",
			register: "S'inscrire",
			submit_project: "Déposer un projet",
			create_project: "Créer un projet",
			my_projects: "Mes projets déposés",
			personnal_informations: "Informations personnelles",
			who_we_are: "Qui sommes-nous ?",
			marketplace: "Marketplace",
			projects: "Nos films",
			contact: "Contact",
			faq: "FAQ",
			subscribe: "Offres Incubation",
			all_projects: "Tous les projets",
			logout: "Se déconnecter",
			call_for_project: "Les petites annonces",
			coming_soon: {
				title: "Votez pour le Prix Alice Guy 2025",
				message: "Depuis 2018, le Prix Alice Guy récompense le meilleur film français réalisé par une femme.",
				submessage: "Participez en votant pour vos 5 films préférés avant le 31 janvier 2025.",
				button: "Je participe au vote",
			},
		},
		footer: {
			faq: "FAQ",
			faq_link: "https://www.clapaction.com/#section-FAQ",
			social_links: "Réseaux sociaux",
			neeedco: "Le réseau social du cinéma",
			dcf: "Le comité de sélection",
			contact: "Contact",
			legal_notice: "Mentions légales",
			privacy_policy: "Politique de confidentialité",
			terms_of_use: "Conditions d'utilisation",
			copyright: "Copyright © {{year}} clapAction",
		},
		suscribe: {
			suscribe_newsletter: "S'inscrire à la newsletter",
			suscribed_newsletter: "Vous êtes bien inscrit à la newsletter clapAction !",
		},
	},
	enums: {
		EValidationErrors: EValidationErrorImplementation,
		EProjectThemes: EProjectThemeImplementation,
		EProjectFormats: EProjectFormatImplementation,
		EProjectReasons: EProjectReasonImplementation,
		EProjectPurposes: EProjectPurposeImplementation,
	},
};

export default trads;
